import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { Button, Label, TextInput } from 'flowbite-react';
import { GoogleGenAI, createPartFromUri, ContentListUnion } from '@google/genai';
import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
// Use Vite to load worker as classic script URL to avoid HMR overlays in worker
// @ts-ignore - url import for worker
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

import ProgressBar from '../../components/ProgressBar';
import { request } from '../../utils/remote';

// Configure pdf.js to use classic worker by URL
(pdfjsLib as any).GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

async function extractPdfTextRange(file: File, startPage: number, endPage: number): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = (pdfjsLib as any).getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;
  const total = pdf.numPages as number;
  const s = Math.max(1, Math.min(startPage, total));
  const e = Math.max(s, Math.min(endPage, total));
  let chunks: string[] = [];
  for (let p = s; p <= e; p++) {
    const page = await pdf.getPage(p);
    const content = await page.getTextContent();
    const text = (content.items || []).map((it: any) => it.str).join(' ');
    chunks.push(`\n\n=== Page ${p} ===\n` + text);
  }
  try { await pdf.destroy?.(); } catch {}
  return chunks.join('\n');
}

// Helpers for chunking by page with a single loaded PDF
async function loadPdfDocument(file: File): Promise<any> {
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = (pdfjsLib as any).getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;
  return pdf;
}

async function extractPageText(pdf: any, pageNumber: number): Promise<string> {
  const page = await pdf.getPage(pageNumber);
  const content = await page.getTextContent();
  const text = (content.items || []).map((it: any) => it.str).join(' ');
  return text;
}

// Get the PDF outline (bookmark tree) and resolve page numbers when possible
async function getPdfOutlineTree(pdf: any): Promise<any[]> {
  const outline = await pdf.getOutline();
  if (!outline || !Array.isArray(outline)) return [];

  async function resolveItem(item: any): Promise<any> {
    let page: number | null = null;
    try {
      let dest = item?.dest;
      let destArray: any = null;
      if (typeof dest === 'string') {
        // named destination; resolve to explicit dest array
        try { destArray = await pdf.getDestination(dest); } catch (e) { destArray = null; }
      } else if (Array.isArray(dest)) {
        destArray = dest;
      }

      if (Array.isArray(destArray) && destArray.length > 0) {
        const first = destArray[0];
        try {
          // If first is a ref to a page object, getPageIndex resolves it to 0-based index
          const idx = await (pdf as any).getPageIndex(first);
          if (Number.isFinite(idx)) page = idx + 1;
        } catch (e) {
          // fallback: sometimes first may already be a page number
          if (typeof first === 'number') page = first;
        }
      }
    } catch (e) {
      // ignore resolution errors
      page = null;
    }

    const children: any[] = [];
    if (Array.isArray(item.items) && item.items.length > 0) {
      for (const it of item.items) {
        children.push(await resolveItem(it));
      }
    }

    return { title: item?.title ?? '', page, url: item?.url ?? null, items: children };
  }

  const out: any[] = [];
  for (const it of outline) out.push(await resolveItem(it));
  return out;
}

type ChapterRow = {
  name: string;
  printedStart?: number | null;
  printedEnd?: number | null;
  alignedStart?: number | null;
  alignedEnd?: number | null;
  // optional nested subchapters when provided by Gemini (JSON-assisted mode)
  subchapters?: ChapterRow[];
};

type ChunkItem = {
  chapterIndex: number;
  chapterName: string;
  page: number;
  text: string;
  // For university mode: optional parent chapter index and subchapter index
  parentChapterIndex?: number | null;
  subchapterIndex?: number | null;
  parentChapterTitle?: string | null;
  subchapterTitle?: string | null;
};

const RagSetup: React.FC = () => {
  const { role, token } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [bookName, setBookName] = useState<string>('');
  const [grades, setGrades] = useState<Array<any>>([]);
  const [subjects, setSubjects] = useState<Array<any>>([]);
  const [selectedGrade, setSelectedGrade] = useState<number | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<number | null>(null);
  // Institution mode: 'school' uses grade->subject, 'university' uses field->year->course
  const [institutionMode, setInstitutionMode] = useState<'school' | 'university'>('school');
  const [fields, setFields] = useState<Array<{ id: number; name: string }>>([]);
  const [fieldsLoading, setFieldsLoading] = useState<boolean>(false);
  const [selectedField, setSelectedField] = useState<number | null>(null);
  const [academicYears, setAcademicYears] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<number | null>(null);
  const [courses, setCourses] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedCourse, setSelectedCourse] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // New state for Gemini and TOC input
  const [apiKey, setApiKey] = useState<string>('');
  const [tocStart, setTocStart] = useState<string>('');
  const [tocEnd, setTocEnd] = useState<string>('');
  const [useImageMode, setUseImageMode] = useState<boolean>(false);
  // JSON-assisted mode: when enabled (university admins), user uploads a companion JSON
  // describing chapters/subchapters. Gemini will be asked to map those entries to PDF pages.
  const [useJsonMode, setUseJsonMode] = useState<boolean>(false);
  const [jsonFile, setJsonFile] = useState<File | null>(null);
  const [sendBookmarkTree, setSendBookmarkTree] = useState<boolean>(false);
  const [pagesPerUpload, setPagesPerUpload] = useState<string>('4');
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ name: string; uri?: string; mimeType?: string; state?: string; error?: string; blob?: Blob; start?: number; end?: number }>>([]);
  const [uploadConcurrency, setUploadConcurrency] = useState<number>(2);
  const [showChunkModeModal, setShowChunkModeModal] = useState<boolean>(false);
  const [chunkModeChoice, setChunkModeChoice] = useState<'text' | 'image'>('text');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [useStaging, setUseStaging] = useState<boolean>(false);

  // Chapters parsed from Gemini and alignment handling
  const [chapters, setChapters] = useState<ChapterRow[]>([]);
  // The user enters which PDF page corresponds to printed page 1 (e.g., if printed page 1 is on PDF page 7, enter 7)
  const [alignBaseInput, setAlignBaseInput] = useState<string>('');
  const [lastChapterEndInput, setLastChapterEndInput] = useState<string>('');
  // Wizard state
  const [currentStep, setCurrentStep] = useState<number>(1); // 1: select PDF, 2: preview + TOC, 3: chapters + align
  const [showStep3Preview, setShowStep3Preview] = useState<boolean>(false);
  const [alignmentNotice, setAlignmentNotice] = useState<string | null>(null);
  const [forceRerender, setForceRerender] = useState<number>(0);

  // Debug: log when chapters state changes so we can confirm UI gets updated
  useEffect(() => {
    try {
      console.log('[RagSetup] chapters state changed: count=', (chapters || []).length, 'sample=', JSON.stringify((chapters || []).slice(0,3).map(c => ({ name: c.name, alignedStart: c.alignedStart, alignedEnd: c.alignedEnd })), null, 2));
      // ensure a render tick occurs so controlled inputs refresh
      setForceRerender((n) => n + 1);
    } catch (e) {
      console.log('[RagSetup] chapters state changed (could not stringify sample)');
    }
  }, [chapters]);
  // Mapping state: DB chapters for selected subject and user-selected mappings
  const [dbChapters, setDbChapters] = useState<Array<any>>([]);
  // For university flow: course-level subchapters fetched separately
  const [courseSubchapters, setCourseSubchapters] = useState<Array<{ id: number; name: string }>>([]);
  // mapping for gemini-provided subchapters: key `${chapterIdx}_${subIdx}` -> db subchapter id or null
  const [subchapterMappings, setSubchapterMappings] = useState<Record<string, number | null>>({});
  const [chapterMappings, setChapterMappings] = useState<Record<number, number | null>>({});
  // Chunking state
  const [isChunking, setIsChunking] = useState<boolean>(false);
  const [chunkProgress, setChunkProgress] = useState<{ completed: number; total: number; label: string }>({ completed: 0, total: 0, label: '' });
  const [chunkSummaries, setChunkSummaries] = useState<{ name: string; start: number; end: number; chunks: number }[]>([]);
  const [chunkItems, setChunkItems] = useState<ChunkItem[]>([]);
  // Embedding state
  const [isEmbedding, setIsEmbedding] = useState<boolean>(false);
  const [embedProgress, setEmbedProgress] = useState<{ completed: number; total: number; label: string }>({ completed: 0, total: 0, label: '' });
  const [embedSummaries, setEmbedSummaries] = useState<{ chapterName: string; count: number }[]>([]);
  // Store embedding details (per-chapter) so we can preview embeddings after run
  const [embedDetails, setEmbedDetails] = useState<Record<string, Array<{ page: number; text: string; raw: any }>>>({});
  const [openPreviewChapters, setOpenPreviewChapters] = useState<Record<string, boolean>>({});
  const [showApiKeyModal, setShowApiKeyModal] = useState<boolean>(false);
  const [isSavingKey, setIsSavingKey] = useState<boolean>(false);

  // Normalize embedding response items into a small preview object so UI can show something
  function normalizeEmbeddingItem(item: any) {
    if (!item) return { note: 'no data' };
    // common shapes: { embedding: number[] } or { embeddings: [...]} or raw array
    let arr: any[] | null = null;
    if (Array.isArray(item) && item.length > 0 && typeof item[0] === 'number') arr = item as any[];
    else if (Array.isArray(item?.values)) arr = item.values;
    else if (Array.isArray(item?.embedding)) arr = item.embedding;
    else if (Array.isArray(item?.vector)) arr = item.vector;
    else if (Array.isArray(item?.output?.embedding)) arr = item.output.embedding;
    else if (Array.isArray(item?.embeddings)) {
      // embeddings can be an array of numbers or an array of objects with .values/.embedding/.vector
      const first = item.embeddings[0];
      if (Array.isArray(first)) arr = first;
      else if (Array.isArray(first?.values)) arr = first.values;
      else if (Array.isArray(first?.embedding)) arr = first.embedding;
      else if (Array.isArray(first?.vector)) arr = first.vector;
      else {
        // fallback: if embeddings is already numeric array (rare), use it
        if (item.embeddings.every((el: any) => typeof el === 'number')) arr = item.embeddings;
      }
    }
    else if (Array.isArray(item?.data)) {
      // sometimes data is an array containing embedding objects
      const cand = item.data.find((d:any) => Array.isArray(d?.embedding) || Array.isArray(d?.values) || Array.isArray(d?.vector));
      if (cand) arr = cand.embedding ?? cand.values ?? cand.vector ?? null;
    }
    if (arr && Array.isArray(arr)) {
      return { dim: arr.length, sample: arr.slice(0, 6), embedding_vector: arr };
    }
    // If there is a numeric field count or shape metadata
    if (typeof item?.length === 'number') return { dim: item.length, sample: Array.isArray(item) ? item.slice(0, 6) : undefined };
    // Fallback: return small object with some known properties
    const keys = Object.keys(item || {}).slice(0, 6);
    const small: any = { note: 'unrecognized shape', keys };
    return small;
  }

  useEffect(() => {
    // cleanup object URL on unmount or file change
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Fetch grades for the user's institution when token is available
  useEffect(() => {
    const loadGrades = async () => {
      if (!token) return;
      try {
        try {
          const data = await request('/api/school/grades/', { headers: { 'Authorization': `Bearer ${token}` } })
          // data.grades expected
          setGrades(Array.isArray(data?.grades) ? data.grades : [])
        } catch (e) {
          // ignore
        }
      } catch (e) {
        console.warn('Failed to load grades', e);
      }
    };
    loadGrades();
  }, [token]);

  // Load fields of study for institution if switching to university mode
  useEffect(() => {
    const loadFields = async () => {
      if (!token) return;
      if (institutionMode !== 'university') return;
      setFieldsLoading(true);
      try {
        // Need institution id from user info - fetch user info endpoint
        let instId: any = null
        try {
          const ud = await request('/api/user-info/', { headers: { Authorization: token ? `Bearer ${token}` : '' } })
          instId = ud?.institution_id
        } catch (e) {
          setFields([])
          setFieldsLoading(false)
          return
        }
        let fetched: Array<any> = [];
        if (!instId || role === 'superuser') {
          // Superuser should be able to load all fields across institutions
          fetched = await (await import('../../utils/remote')).adminGetAllFields();
          // normalize to id/name shape
          fetched = (fetched || []).map((f: any) => ({ id: f.id, name: f.name }));
        } else {
          fetched = await (await import('../../utils/remote')).adminGetInstitutionFields(instId);
        }
        setFields(fetched || []);
      } catch (e) {
        console.warn('Failed to load fields for institution', e);
        setFields([]);
      } finally {
        setFieldsLoading(false);
      }
    };
    loadFields();
  }, [token, institutionMode]);

  // When selectedField changes, load academic years
  useEffect(() => {
    const loadYears = async () => {
      if (!token || !selectedField) {
        setAcademicYears([]);
        setSelectedAcademicYear(null);
        return;
      }
      try {
        let instId: any = null
        try {
          const ud = await request('/api/user-info/', { headers: { Authorization: token ? `Bearer ${token}` : '' } })
          instId = ud?.institution_id
        } catch (e) {
          return
        }
        if (!instId) return;
        const years = await (await import('../../utils/remote')).adminGetFieldAcademicYears(instId, selectedField);
        setAcademicYears(years || []);
      } catch (e) {
        console.warn('Failed to load academic years', e);
        setAcademicYears([]);
      }
    };
    loadYears();
  }, [token, selectedField]);

  // When academic year changes, load courses
  useEffect(() => {
    const loadCourses = async () => {
      if (!token || !selectedAcademicYear || !selectedField) {
        setCourses([]);
        setSelectedCourse(null);
        return;
      }
      try {
        let instId: any = null
        try {
          const ud = await request('/api/user-info/', { headers: { Authorization: token ? `Bearer ${token}` : '' } })
          instId = ud?.institution_id
        } catch (e) {
          return
        }
        if (!instId) return;
        const cs = await (await import('../../utils/remote')).adminGetAcademicYearCourses(instId, selectedField!, selectedAcademicYear);
        setCourses(cs || []);
      } catch (e) {
        console.warn('Failed to load courses', e);
        setCourses([]);
      }
    };
    loadCourses();
  }, [token, selectedAcademicYear, selectedField]);

  // When selectedGrade changes, fetch subjects for that grade
  useEffect(() => {
    const loadSubjects = async () => {
      if (!token || !selectedGrade) {
        setSubjects([]);
        return;
      }
      try {
        try {
          const data = await request(`/api/school/grades/${selectedGrade}/subjects/`, { headers: { 'Authorization': `Bearer ${token}` } })
          setSubjects(Array.isArray(data?.subjects) ? data.subjects : [])
        } catch (e) {
          setSubjects([])
        }
      } catch (e) {
        console.warn('Failed to load subjects for grade', selectedGrade, e);
        setSubjects([]);
      }
    };
    loadSubjects();
  }, [token, selectedGrade]);

  // When selectedSubject changes, fetch DB chapters for mapping step
  useEffect(() => {
    const loadDbChapters = async () => {
      // Only load DB chapters when we have both a selected subject AND a selected grade
      // Do not load subject chapters when operating in university mode or when the user is a university admin
      if (role === 'college_university' || institutionMode === 'university') {
        // In university mode, load course chapters (which include subchapters) when a course is selected
        if (!token || !selectedCourse) {
          setDbChapters([]);
          setCourseSubchapters([]);
          setChapterMappings({});
          return;
        }
        try {
          const remote = await import('../../utils/remote');
          const resp = await remote.getCourseChapters(token ? `Bearer ${token}` : null, selectedCourse);
          const list = Array.isArray(resp?.chapters) ? resp.chapters : [];
          setDbChapters(list.map((c:any) => ({ id: c.id, name: c.name })));
          // collect subchapters for the selected course (flattened)
          const subs: Array<{ id: number; name: string }> = [];
          for (const ch of list) {
            if (Array.isArray(ch.subchapters)) {
              for (const s of ch.subchapters) subs.push({ id: s.id, name: s.name });
            }
          }
          setCourseSubchapters(subs);
        } catch (e) {
          console.warn('Failed to load course chapters for university mode', e);
          setDbChapters([]);
          setCourseSubchapters([]);
        }
        // initialize mappings if chapters already exist: null for each parsed chapter
        setChapterMappings((prev) => {
          const next: Record<number, number | null> = {};
          (chapters || []).forEach((_, idx) => { next[idx] = prev[idx] ?? null; });
          return next;
        });
        return;
      }
      if (!token || !selectedSubject || !selectedGrade) {
        setDbChapters([]);
        setChapterMappings({});
        return;
      }
      try {
        // Use the subject-only chapters endpoint (server returns chapters with school_grade_ids).
        // We avoid issuing a grade/subject-scoped request to prevent noisy 404s when that endpoint
        // is not present on the backend. We will filter client-side by selectedGrade below.
        let data: any = null
        try {
          data = await request(`/api/school/subjects/${selectedSubject}/chapters/`, { headers: { 'Authorization': `Bearer ${token}` } })
        } catch (e) {
          setDbChapters([])
          setChapterMappings({})
          return
        }
        const list = Array.isArray(data?.chapters) ? data.chapters : [];
  // Only keep chapters that are associated with the selected grade (school_grade_ids contains selectedGrade)
  const filtered = list.filter((c: any) => Array.isArray(c?.school_grade_ids) && c.school_grade_ids.includes(selectedGrade));
  setDbChapters(filtered);

        // initialize mappings if chapters already exist: null for each parsed chapter
        setChapterMappings((prev) => {
          const next: Record<number, number | null> = {};
          (chapters || []).forEach((_, idx) => { next[idx] = prev[idx] ?? null; });
          return next;
        });
      } catch (e) {
        console.warn('Failed to load DB chapters for subject', selectedSubject, e);
        setDbChapters([]);
        setChapterMappings({});
      }
    };
    loadDbChapters();
  }, [token, selectedSubject, selectedGrade, chapters]);

  // On mount / token change: try to fetch saved API key for user
  useEffect(() => {
    const tryFetchKey = async () => {
      if (!token) return;
      try {
        try {
          const data = await request('/api/user/gemini-key/', { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } })
          if (data?.has_key && data?.api_key) {
            setApiKey(data.api_key || '');
          } else {
            setShowApiKeyModal(true);
          }
        } catch (e) {
          // ignore network errors; user can manually paste key
          console.warn('Failed to fetch saved Gemini key', e);
        }
      } catch (e) {
        // double-catch (keeps structure similar)
      }
    };
    tryFetchKey();
  }, [token]);

  // Save API key to server
  const saveApiKeyToServer = async (key: string) => {
    if (!token) return false;
    setIsSavingKey(true);
    try {
      await request('/api/user/gemini-key/', { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ api_key: key }) })
      setIsSavingKey(false);
      setShowApiKeyModal(false);
      setApiKey(key);
      return true;
    } catch (e) {
      console.warn('Error saving API key', e);
      setIsSavingKey(false);
      return false;
    }
  };

  function toIntOrNull(v: any): number | null {
    if (v === null || v === undefined) return null;
    const num = parseInt(String(v).replace(/[^0-9-]/g, ''), 10);
    return Number.isFinite(num) ? num : null;
  }

  function parseChaptersFromOutput(raw: any, base?: number): ChapterRow[] {
    const arr: any[] = Array.isArray(raw) ? raw : [];
    const baseNum = typeof base === 'number' && Number.isFinite(base) && base > 0 ? base : undefined;

    // Recursive helper to parse a single item possibly containing nested subchapters
    function parseItem(it: any): ChapterRow {
      const name = (it?.name ?? '').toString();
      const printedStart = toIntOrNull((it && (it['start=page'] ?? it.start_page ?? it.start)) as any);
      const printedEnd = toIntOrNull((it && (it['end-age'] ?? it.end_page ?? it.end)) as any);
      const alignedStart = baseNum && printedStart != null ? baseNum + printedStart - 1 : (it?.alignedStart ?? null);
      const alignedEnd = baseNum && printedEnd != null ? baseNum + printedEnd - 1 : (it?.alignedEnd ?? null);

      // Detect subchapter arrays with common keys
      const rawSubs = it?.subchapters ?? it?.sub_chapters ?? it?.children ?? it?.parts ?? [];
      const subchapters = Array.isArray(rawSubs) && rawSubs.length > 0 ? rawSubs.map((s: any) => parseItem(s)) : undefined;

      return { name, printedStart, printedEnd, alignedStart, alignedEnd, subchapters } as ChapterRow;
    }

    return arr.map((it) => parseItem(it));
  }

  async function applyAlignment(baseNum: number) {
    // Apply numeric base alignment and ensure the last chapter's aligned end
    // becomes the PDF's last page when the end is missing/invalid.
    console.log('[RagSetup] applyAlignment: called with baseNum=', baseNum, 'filePresent=', !!file);
    setError(null);
    if (!Number.isFinite(baseNum) || baseNum <= 0) {
      console.log('[RagSetup] applyAlignment: invalid baseNum, aborting');
      return;
    }
    // If we don't have a file, just apply the base offset without total-page fixes
    if (!file) {
      setChapters((prev) => prev.map((row) => ({
        ...row,
        // Option 1: treat existing alignedStart/End as printed page numbers when printedStart isn't present
        alignedStart: row.printedStart != null ? baseNum + row.printedStart - 1 : (row.alignedStart != null ? baseNum + row.alignedStart - 1 : null),
        alignedEnd: row.printedEnd != null ? baseNum + row.printedEnd - 1 : (row.alignedEnd != null ? baseNum + row.alignedEnd - 1 : null),
      })));
      return;
    }

    // Load PDF to determine total pages so we can set the last chapter end correctly
    try {
      const pdf = await loadPdfDocument(file);
      const totalPages = Number(pdf.numPages) || 0;

  setChapters((prev) => {
        const lastIndex = prev.length - 1;

        // recursive helper to align a subchapter (and its nested subchapters)
        function alignSub(sub: ChapterRow): ChapterRow {
          // Option 1: If printedStart exists, use it; otherwise treat existing alignedStart as printed and convert.
          let sStart = sub.printedStart != null ? baseNum + sub.printedStart - 1 : (sub.alignedStart != null ? baseNum + sub.alignedStart - 1 : null);
          let sEnd = sub.printedEnd != null ? baseNum + sub.printedEnd - 1 : (sub.alignedEnd != null ? baseNum + sub.alignedEnd - 1 : null);
          if (sEnd != null && totalPages > 0) sEnd = Math.min(sEnd, totalPages);
          if (sStart != null) sStart = Math.max(1, sStart);
          let nested: ChapterRow[] | undefined = undefined;
          if (Array.isArray(sub.subchapters) && sub.subchapters.length > 0) {
            nested = sub.subchapters.map((ss) => alignSub(ss));
          }
          return { ...sub, alignedStart: sStart, alignedEnd: sEnd, subchapters: nested } as ChapterRow;
        }

        const updated = prev.map((row, idx) => {
          // Align the chapter itself
          // Option 1: If printedStart exists, use it; otherwise treat existing alignedStart as printed and convert.
          let alignedStart = row.printedStart != null ? baseNum + row.printedStart - 1 : (row.alignedStart != null ? baseNum + row.alignedStart - 1 : null);
          let alignedEnd = row.printedEnd != null ? baseNum + row.printedEnd - 1 : (row.alignedEnd != null ? baseNum + row.alignedEnd - 1 : null);

          // If this is the last chapter and we have a PDF, always set its alignedEnd
          // to the last PDF page so the chapter covers until the end of the book.
          if (idx === lastIndex && alignedStart != null) {
            alignedEnd = totalPages > 0 ? totalPages : alignedEnd;
          }

          // Clamp values into PDF bounds for chapter
          if (alignedEnd != null && totalPages > 0) alignedEnd = Math.min(alignedEnd, totalPages);
          if (alignedStart != null) alignedStart = Math.max(1, alignedStart);

          // Align nested subchapters recursively
          let newSubs: ChapterRow[] | undefined = undefined;
          if (Array.isArray(row.subchapters) && row.subchapters.length > 0) {
            newSubs = row.subchapters.map((sub) => alignSub(sub));
          }

          // return aligned chapter row
          return { ...row, alignedStart, alignedEnd, subchapters: newSubs } as ChapterRow;
        });

        // commit updated chapters
        return updated;
      });
    } catch (e) {
      // Fallback: apply alignment without total page knowledge
      setChapters((prev) => prev.map((row) => ({
        ...row,
        // Option 1: when printedStart not present, treat existing alignedStart as printed and convert
        alignedStart: row.printedStart != null ? baseNum + row.printedStart - 1 : (row.alignedStart != null ? baseNum + row.alignedStart - 1 : null),
        alignedEnd: row.printedEnd != null ? baseNum + row.printedEnd - 1 : (row.alignedEnd != null ? baseNum + row.alignedEnd - 1 : null),
      })));
    }
  }

  // Set the alignedEnd of the last chapter to a specific PDF page number
  const setLastChapterEnd = async () => {
    setError(null);
    const p = parseInt(lastChapterEndInput, 10);
    if (!Number.isFinite(p) || p <= 0) {
      setError('Please enter a valid PDF page number (>= 1) for the last chapter end.');
      return;
    }

    let totalPages = 0;
    if (file) {
      try {
        const pdf = await loadPdfDocument(file);
        totalPages = Number(pdf.numPages) || 0;
        try { await pdf.destroy?.(); } catch {}
      } catch (e) {
        console.warn('Failed to load PDF to validate last page', e);
      }
    }

    const clamped = totalPages > 0 ? Math.min(Math.max(1, p), totalPages) : Math.max(1, p);

    setChapters((prev) => {
      if (!prev || prev.length === 0) return prev;
      const lastIdx = prev.length - 1;
      const updated = prev.map((r, i) => i === lastIdx ? { ...r, alignedEnd: clamped } : r);
      console.log('[RagSetup] setLastChapterEnd: set last chapter end to', clamped);
      return updated;
    });
    // force re-render to ensure UI updates
    setForceRerender((n) => n + 1);
    setAlignmentNotice('Last chapter end updated');
    setTimeout(() => setAlignmentNotice(null), 3000);
  };

  // Create a new PDF Blob containing only pages [startPage..endPage] (1-based)
  async function createPdfForRange(file: File, startPage: number, endPage: number): Promise<Blob> {
    const arrayBuffer = await file.arrayBuffer();
    const srcDoc = await PDFDocument.load(arrayBuffer);
    const dstDoc = await PDFDocument.create();
    const total = srcDoc.getPageCount();
    const s = Math.max(1, Math.min(startPage, total));
    const e = Math.max(s, Math.min(endPage, total));
    // copyPages uses zero-based indices
    const indices = [] as number[];
    for (let i = s - 1; i <= e - 1; i++) indices.push(i);
    const pages = await dstDoc.copyPages(srcDoc, indices);
    pages.forEach((page: any) => dstDoc.addPage(page));
    const bytes = await dstDoc.save();
    // Ensure we pass an ArrayBuffer or Uint8Array to Blob
    const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    // cast buffer to ArrayBuffer to satisfy TS in environments with ArrayBufferLike
    return new Blob([u8.buffer as unknown as ArrayBuffer], { type: 'application/pdf' });
  }

    // (Bookmark tree logging helper removed; replaced by checkbox-driven send behavior)

  // Image-based chunking: for each chapter range, create per-page or per-chunk PDF(s), upload to Gemini,
  // ask Gemini to extract all text on the page(s) and return JSON like [{page: '1', text: '...'}, ...]
  async function handleChunkChaptersImageMode() {
    if (!file) { setError('Please choose a PDF file first.'); return; }
    if (!chapters || chapters.length === 0) { setError('No chapters to chunk.'); return; }
    if (!apiKey.trim()) { setError('Please enter your Gemini API key.'); return; }
    setIsChunking(true);
    try {
      const aiOptions: any = { apiKey: apiKey.trim() };
      if (useStaging) aiOptions.httpOptions = { baseUrl: 'https://autopush-generativelanguage.sandbox.googleapis.com' };
      const ai = new GoogleGenAI(aiOptions);

      // Prepare ranges from chapters (alignedStart..alignedEnd) and create PDF blobs split by pagesPerUpload
      const per = Math.max(1, parseInt(pagesPerUpload || '1', 10));
  // no need to load full PDF here; createPdfForRange will handle page extraction as needed

      // Build an array of page ranges to process (chapter by chapter)
      const chapterRanges = (chapters || []).map((c, idx) => ({ idx, name: c.name || `Chapter ${idx+1}`, start: Number(c.alignedStart || 0), end: Number(c.alignedEnd || 0) })).filter(r => r.start>0 && r.end>=r.start);
      let allPagesToProcess: { chapterIdx: number; page: number }[] = [];
      for (const cr of chapterRanges) {
        for (let p = cr.start; p <= cr.end; p++) allPagesToProcess.push({ chapterIdx: cr.idx, page: p });
      }
      if (allPagesToProcess.length === 0) { setError('No pages found to process. Ensure chapters are aligned.'); setIsChunking(false); return; }

      setChunkProgress({ completed: 0, total: allPagesToProcess.length, label: 'Preparing uploads…' });

      // Group pages into groups of `per` and upload each group as a PDF
      const groups: { pages: number[]; chapterIdxs: number[] }[] = [];
      for (let i = 0; i < allPagesToProcess.length; i += per) {
        const slice = allPagesToProcess.slice(i, i + per);
        const pages = slice.map(s => s.page);
        const chapterIdxs = Array.from(new Set(slice.map(s => s.chapterIdx)));
        groups.push({ pages, chapterIdxs });
      }

      let processed = 0;
      const newChunkItems: ChunkItem[] = [];

      // Create blobs for every group first
      const groupsWithBlobs: Array<{ name: string; blob: Blob; start: number; end: number }> = [];
      for (let gi = 0; gi < groups.length; gi++) {
        const g = groups[gi];
        const gStart = g.pages[0];
        const gEnd = g.pages[g.pages.length - 1];
        const blob = await createPdfForRange(file, gStart, gEnd);
        groupsWithBlobs.push({ name: `pages_${gStart}-${gEnd}.pdf`, blob, start: gStart, end: gEnd });
      }

      // register uploadedFiles entries
      setUploadedFiles(groupsWithBlobs.map((b) => ({ name: b.name, state: 'PENDING', blob: b.blob, start: b.start, end: b.end })));

      // upload in parallel with concurrency
      const uploadResults = await uploadFilesWithConcurrency(ai, groupsWithBlobs.map(g => ({ name: g.name, blob: g.blob, start: g.start, end: g.end })), uploadConcurrency);

      // for each result, request text extraction
      for (let i = 0; i < uploadResults.length; i++) {
        const res = uploadResults[i];
        const g = groupsWithBlobs[i];
        if (!res) {
          setUploadedFiles((prev) => prev.map((f) => (f.name === g.name ? { ...f, state: 'FAILED', error: 'Upload failed' } : f)));
          continue;
        }
        if (!res.uri) {
          setUploadedFiles((prev) => prev.map((f) => (f.name === g.name ? { ...f, state: 'FAILED', error: 'No URI returned' } : f)));
          continue;
        }
        setUploadedFiles((prev) => prev.map((f) => (f.name === g.name ? { ...f, state: 'READY', uri: res.uri, mimeType: res.mimeType } : f)));

        const extractPrompt = `Extract all visible text from the uploaded PDF file. For each PDF page return an object with keys {"page": "<page number>", "text": "<extracted text>"}. Return ONLY a JSON array like [{"page":"1","text":"..."}, ...]. Page numbers should refer to the PDF page number within the original book.`;
        const contents: ContentListUnion = [extractPrompt, createPartFromUri(res.uri, res.mimeType || 'application/pdf')];
        setChunkProgress({ completed: processed, total: allPagesToProcess.length, label: `Requesting text extraction for pages ${g.start}-${g.end}…` });
        let genResp: any = null;
        try {
          genResp = await ai.models.generateContent({ model: 'gemini-2.5-flash', contents: contents as any });
        } catch (genErr: any) {
          console.error('Gemini extraction error', genErr);
          setUploadedFiles((prev) => prev.map((f) => (f.name === g.name ? { ...f, state: 'FAILED', error: genErr?.message || String(genErr) } : f)));
          continue;
        }
        const out = genResp?.text ?? '';
        // Parse JSON array and map sequentially to the group's original pages when explicit page fields are missing.
        try {
          const jsonStart = out.indexOf('[');
          const jsonEnd = out.lastIndexOf(']');
          const jsonStr = jsonStart >= 0 && jsonEnd >= 0 ? out.slice(jsonStart, jsonEnd + 1) : out;
          const parsed: any[] = JSON.parse(jsonStr || '[]');

          // Always map parsed array entries sequentially to the group's original pages.
          // parsed[0] -> g.start, parsed[1] -> g.start + 1, etc. (Ignore Gemini-provided page numbers.)
          for (let idxParsed = 0; idxParsed < parsed.length; idxParsed++) {
            const obj = parsed[idxParsed];
            const text = (obj?.text ?? obj?.content ?? '').toString();
            const pnum = g.start + idxParsed; // sequential mapping

            // create chunk item for the correct chapter (find chapter that contains this page)
            const chapterIndex = (chapters || []).findIndex(ch => Number(ch.alignedStart) <= pnum && Number(ch.alignedEnd) >= pnum);
            const chName = chapterIndex >= 0 ? (chapters || [])[chapterIndex].name : `Unknown`;
            newChunkItems.push({ chapterIndex: chapterIndex >= 0 ? chapterIndex : 0, chapterName: chName, page: pnum, text });
            processed += 1;
            setChunkProgress({ completed: processed, total: allPagesToProcess.length, label: `Processed page ${pnum}` });
          }
        } catch (parseErr) {
          console.error('Failed to parse Gemini page-extract output', out, parseErr);
          setUploadedFiles((prev) => prev.map((f) => (f.name === g.name ? { ...f, state: 'FAILED', error: 'Invalid JSON from Gemini' } : f)));
          // do not abort the whole run; continue to next file
          continue;
        }
      }

      // merge newChunkItems into existing chunkItems state and summaries
      setChunkItems(newChunkItems);
      // build summaries
      const summaryMap = new Map<string, { name: string; start: number; end: number; chunks: number }>();
      for (const ci of newChunkItems) {
        const key = ci.chapterName;
        const cur = summaryMap.get(key) ?? { name: key, start: ci.page, end: ci.page, chunks: 0 };
        cur.start = Math.min(cur.start, ci.page);
        cur.end = Math.max(cur.end, ci.page);
        cur.chunks += 1;
        summaryMap.set(key, cur);
      }
      setChunkSummaries(Array.from(summaryMap.values()));
      setChunkProgress({ completed: processed, total: allPagesToProcess.length, label: 'Completed' });
    } catch (e: any) {
      console.error('Image chunking error', e);
      setError(e?.message || 'Failed to perform image-based chunking.');
    } finally {
      setIsChunking(false);
    }
  }

  // Upload a file blob to Gemini via GoogleGenAI client and poll until processed (or failed)
  async function uploadFileToGemini(ai: any, blob: Blob, displayName: string) {
    try {
      const file = await ai.files.upload({ file: blob, config: { displayName } });
      let getFile = await ai.files.get({ name: file.name });
      // poll until processing completes
      while (getFile.state === 'PROCESSING') {
        // update status
        setUploadedFiles((prev) => prev.map((f) => (f.name === file.name ? { ...f, state: 'PROCESSING' } : f)));
        await new Promise((resolve) => setTimeout(resolve, 2500));
        getFile = await ai.files.get({ name: file.name });
      }
      if (getFile.state === 'FAILED') {
        setUploadedFiles((prev) => prev.map((f) => (f.name === file.name ? { ...f, state: 'FAILED', error: 'File processing failed' } : f)));
        throw new Error('File processing failed for ' + file.name);
      }
      // mark ready
      setUploadedFiles((prev) => prev.map((f) => (f.name === file.name ? { ...f, state: getFile.state, uri: getFile.uri, mimeType: getFile.mimeType } : f)));
      return getFile;
    } catch (e: any) {
      // ensure we mark the file as failed if present
      setUploadedFiles((prev) => prev.map((f) => (f.name === displayName ? { ...f, state: 'FAILED', error: e?.message || String(e) } : f)));
      throw e;
    }
  }

  // Upload multiple files with a concurrency limit
  async function uploadFilesWithConcurrency(ai: any, entries: Array<{ name: string; blob: Blob; start?: number; end?: number }>, concurrency: number) {
    const results: Array<any> = new Array(entries.length);
    let idx = 0;
    const runner = async () => {
      while (true) {
        const i = idx++;
        if (i >= entries.length) return;
        const ent = entries[i];
        // mark uploading
        setUploadedFiles((prev) => prev.map((f) => (f.name === ent.name ? { ...f, state: 'UPLOADING' } : f)));
        try {
          const res = await uploadFileToGemini(ai, ent.blob, ent.name);
          results[i] = res;
        } catch (e) {
          results[i] = null;
        }
      }
    };
    const workers = [] as Promise<any>[];
    for (let w = 0; w < Math.max(1, concurrency); w++) workers.push(runner());
    await Promise.all(workers);
    return results;
  }

  // Retry upload for a specific file entry (must have blob)
  const retryUpload = async (entryName: string) => {
    const ent = uploadedFiles.find((f) => f.name === entryName && f.blob);
    if (!ent || !ent.blob) return;
    try {
      setUploadedFiles((prev) => prev.map((f) => (f.name === entryName ? { ...f, state: 'RETRYING', error: undefined } : f)));
      const aiOptions: any = { apiKey: apiKey.trim() };
      if (useStaging) aiOptions.httpOptions = { baseUrl: 'https://autopush-generativelanguage.sandbox.googleapis.com' };
      const ai = new GoogleGenAI(aiOptions);

      // (Re-)upload the blob and wait for processing
      const getFile = await uploadFileToGemini(ai, ent.blob, ent.name);
      if (!getFile || !getFile.uri) {
        setUploadedFiles((prev) => prev.map((f) => (f.name === entryName ? { ...f, state: 'FAILED', error: 'Upload or processing failed' } : f)));
        return;
      }

      // Now re-run the extraction step for this file and merge results into chunkItems
      const extractPrompt = `Extract all visible text from the uploaded PDF file. For each PDF page return an object with keys {"page": "<page number>", "text": "<extracted text>"}. Return ONLY a JSON array like [{"page":"1","text":"..."}, ...]. Page numbers should refer to the PDF page number within the original book.`;
      const contents: ContentListUnion = [extractPrompt, createPartFromUri(getFile.uri, getFile.mimeType || 'application/pdf')];
      setUploadedFiles((prev) => prev.map((f) => (f.name === entryName ? { ...f, state: 'EXTRACTING' } : f)));

      let genResp: any = null;
      try {
        genResp = await ai.models.generateContent({ model: 'gemini-2.5-flash', contents: contents as any });
      } catch (genErr: any) {
        console.error('Gemini extraction error on retry', genErr);
        setUploadedFiles((prev) => prev.map((f) => (f.name === entryName ? { ...f, state: 'FAILED', error: genErr?.message || String(genErr) } : f)));
        return;
      }

      const out = genResp?.text ?? '';
      // parse JSON and map sequentially to the entry's pages
      try {
        const jsonStart = out.indexOf('[');
        const jsonEnd = out.lastIndexOf(']');
        const jsonStr = jsonStart >= 0 && jsonEnd >= 0 ? out.slice(jsonStart, jsonEnd + 1) : out;
        const parsed: any[] = JSON.parse(jsonStr || '[]');

        // Prepare new chunk items for this entry
        const startPage = ent.start ?? 0;
        const newItems: ChunkItem[] = [];
        for (let idxParsed = 0; idxParsed < parsed.length; idxParsed++) {
          const obj = parsed[idxParsed];
          const text = (obj?.text ?? obj?.content ?? '').toString();
          const pnum = startPage + idxParsed;
          const chapterIndex = (chapters || []).findIndex(ch => Number(ch.alignedStart) <= pnum && Number(ch.alignedEnd) >= pnum);
          const chName = chapterIndex >= 0 ? (chapters || [])[chapterIndex].name : `Unknown`;
          newItems.push({ chapterIndex: chapterIndex >= 0 ? chapterIndex : 0, chapterName: chName, page: pnum, text });
        }

        // Merge into chunkItems state and update summaries/progress
        setChunkItems((prev) => {
          const merged = [...prev, ...newItems];
          // update summaries
          const summaryMap = new Map<string, { name: string; start: number; end: number; chunks: number }>();
          for (const ci of merged) {
            const key = ci.chapterName;
            const cur = summaryMap.get(key) ?? { name: key, start: ci.page, end: ci.page, chunks: 0 };
            cur.start = Math.min(cur.start, ci.page);
            cur.end = Math.max(cur.end, ci.page);
            cur.chunks += 1;
            summaryMap.set(key, cur);
          }
          setChunkSummaries(Array.from(summaryMap.values()));
          setChunkProgress((prevP) => ({ completed: merged.length, total: prevP.total, label: `Processed ${merged.length}/${prevP.total}` }));
          return merged;
        });

        // mark uploadedFiles entry as READY
        setUploadedFiles((prev) => prev.map((f) => (f.name === entryName ? { ...f, state: 'READY', uri: getFile.uri, mimeType: getFile.mimeType, error: undefined } : f)));
      } catch (parseErr) {
        console.error('Failed to parse Gemini page-extract output on retry', out, parseErr);
        setUploadedFiles((prev) => prev.map((f) => (f.name === entryName ? { ...f, state: 'FAILED', error: 'Invalid JSON from Gemini' } : f)));
        return;
      }
    } catch (e:any) {
      console.error('Retry upload failed', e);
      setUploadedFiles((prev) => prev.map((f) => (f.name === entryName ? { ...f, state: 'FAILED', error: e?.message || String(e) } : f)));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setError(null);
    if (!f) {
      setFile(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      return;
    }
    // basic validation for PDF
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      setError('Please select a valid PDF file.');
      setFile(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      return;
    }
    // set file and generate preview URL
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const url = URL.createObjectURL(f);
    setPreviewUrl(url);
  };

  // Log the PDF bookmark (outline) structure to the console for debugging
  const logBookmarkTree = async () => {
    setError(null);
    if (!file) {
      setError('Please choose a PDF file first to log its bookmark tree.');
      return;
    }
    try {
      const pdfDoc = await loadPdfDocument(file);
      const outline = await getPdfOutlineTree(pdfDoc);
      try { await pdfDoc.destroy?.(); } catch {}
      console.log('[RagSetup] PDF bookmark/outline JSON:', outline);
      // also pretty-print for environments that support it
      try {
        console.dir?.(outline, { depth: null });
      } catch {}
    } catch (e: any) {
      console.error('[RagSetup] Failed to read PDF outline', e);
      setError('Failed to read PDF outline. See console for details.');
    }
  };

  // Normalize a string into words for matching
  function normalizeWords(s: string) {
    return (s || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean);
  }

  // Find best match candidate by scoring common words and exact-phrase inclusion.
  // candidates: array of { id, name }
  function findBestMatchId(title: string, candidates: Array<any>, minScore = 0.25) : number | null {
    if (!title || !Array.isArray(candidates) || candidates.length === 0) return null;
    const titleWords = normalizeWords(title);
    if (titleWords.length === 0) return null;

    let bestId: number | null = null;
    let bestScore = 0;

    const titlePhrase = title.trim().toLowerCase();

    for (const cand of candidates) {
      const candName = (cand?.name ?? cand?.title ?? '').toString();
      const candWords = normalizeWords(candName);
      if (candWords.length === 0) continue;

      // exact phrase match gets high score
      let score = 0;
      if (candName.toLowerCase().includes(titlePhrase) && titlePhrase.length > 2) score += 1.0;

      // word overlap score (fraction of title words found in candidate)
      const common = titleWords.filter(w => candWords.includes(w)).length;
      const overlap = common / titleWords.length;
      score += overlap; // overlap in [0,1]

      // penalize candidates that are much shorter/longer than title (naive)
      const lenDiff = Math.abs(candWords.length - titleWords.length);
      score = score - (lenDiff * 0.05);

      if (score > bestScore) {
        bestScore = score;
        bestId = cand.id ?? null;
      }
    }

    return bestScore >= minScore ? bestId : null;
  }

  // Auto-match all generated chapters and (optionally) nested subchapters to DB chapters
  const autoMatchAll = () => {
    const newMap: Record<number, number | null> = {};
    const newSubMap: Record<string, number | null> = {};

    // match top-level chapters against dbChapters
    for (let idx = 0; idx < (chapters || []).length; idx++) {
      const c = chapters[idx];
      const best = findBestMatchId(c.name || '', dbChapters || [], 0.25);
      newMap[idx] = best;

      // match nested subchapters against courseSubchapters when in university mode
      if (institutionMode === 'university' && Array.isArray(c.subchapters)) {
        for (let sidx = 0; sidx < c.subchapters.length; sidx++) {
          const sc = c.subchapters[sidx];
          const key = `${idx}_${sidx}`;
          const bestSub = findBestMatchId(sc.name || '', courseSubchapters || [], 0.2);
          newSubMap[key] = bestSub;
        }
      }
    }

    setChapterMappings(newMap);
    if (Object.keys(newSubMap).length > 0) setSubchapterMappings(newSubMap);
  };

  const clearSelection = () => {
    setFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setError(null);
    // also clear the actual input value if needed
    const input = document.getElementById('pdfInput') as HTMLInputElement | null;
    if (input) input.value = '';
  };

  const fileInfo = useMemo(() => {
    if (!file) return null;
    const kb = (file.size / 1024).toFixed(1);
    return `${file.name} (${kb} KB)`;
  }, [file]);

  const handleContinue = async () => {
    setError(null);
    console.log('[RagSetup] handleContinue: invoked', { institutionMode, useImageMode, useJsonMode, tocStart, tocEnd });
    if (!file) {
      console.log('[RagSetup] handleContinue: no file selected - aborting');
      setError('Please choose a PDF file first.');
      return;
    }
    if (!apiKey.trim()) {
      console.log('[RagSetup] handleContinue: missing Gemini API key - prompting modal');
      // Prompt user to add key via modal instead of blocking inline input
      setShowApiKeyModal(true);
      setError('Please add your Gemini API key.');
      return;
    }
    const s = parseInt(tocStart, 10);
    const e = parseInt(tocEnd, 10);
    if (!Number.isFinite(s) || !Number.isFinite(e) || s <= 0 || e <= 0 || e < s) {
      console.log('[RagSetup] handleContinue: invalid TOC range', { s, e });
      setError('Please enter a valid Table of Contents page range (e.g., start 1, end 5; end >= start).');
      return;
    }

    try {
      setIsProcessing(true);
      const aiOptions: any = { apiKey: apiKey.trim() };
      if (useStaging) aiOptions.httpOptions = { baseUrl: 'https://autopush-generativelanguage.sandbox.googleapis.com' };
      const ai = new GoogleGenAI(aiOptions);

      // If JSON-assisted mapping is enabled, send the uploaded JSON + PDF page parts to Gemini
      if (useJsonMode) {
        console.log('[RagSetup] JSON-assisted mode: enabled - beginning JSON-assisted flow');
        if (!jsonFile) {
          console.log('[RagSetup] JSON-assisted mode: no jsonFile provided - aborting');
          setError('Please upload a companion JSON file when JSON-assisted mode is enabled.');
          setIsProcessing(false);
          return;
        }

        // read and validate JSON
        let jsonText = '';
        try {
          jsonText = await jsonFile.text();
          JSON.parse(jsonText); // validate JSON; content will be sent to Gemini
          console.log('[RagSetup] JSON-assisted: uploaded JSON validated (length=' + (jsonText || '').length + ')');
        } catch (je: any) {
          console.error('[RagSetup] JSON-assisted: invalid JSON upload', je);
          setError('Uploaded JSON file is not valid JSON.');
          setIsProcessing(false);
          return;
        }

        const per = Math.max(1, parseInt(pagesPerUpload || '4', 10));
        // If not in image mode, only extract text for the TOC pages and send that with the JSON.
        if (!useImageMode) {
          // 1) Extract text from the specified pages OR send bookmark tree JSON if requested
          console.log('[RagSetup] JSON-assisted (text mode): processing TOC pages', s, e, 'sendBookmarkTree=', sendBookmarkTree);
          // default prompt (text-based)
          let prompt = `You are given a JSON document that lists chapters and optional subchapters for a textbook. The JSON follows nested objects like {name: "...", subchapters: [{name: "..."}]}. Using the provided Table of Contents text (extracted from the PDF pages) determine for each chapter and subchapter the printed page numbers or PDF page numbers where they appear. Return ONLY a JSON array mirroring the input structure but with numeric fields start_page and end_page for each chapter and subchapter. For example: [{"name":"Unit 1","start_page":12,"end_page":34,"subchapters":[{"name":"Section A","start_page":12,"end_page":15}]}]. Do not include any extra text outside the JSON.`;
          let tocTextOrOutline = '';
          if (sendBookmarkTree) {
            // load PDF and resolve outline
            try {
              const pdfDoc = await loadPdfDocument(file);
              const outline = await getPdfOutlineTree(pdfDoc);
              try { await pdfDoc.destroy?.(); } catch {}
              const outlineJson = JSON.stringify(outline || []);
              prompt = `You are given a companion JSON describing the expected chapter structure and a PDF bookmark (outline) JSON. The bookmark/outline JSON uses nested "items" (or "children") arrays to represent subchapters; treat those nested items as subchapters. Using the outline JSON, determine for each TOP-LEVEL chapter and for each nested subchapter (items) the PDF page numbers where they appear. Return ONLY a JSON array mirroring the input structure where each object has the exact keys: name (string), start_page (integer or null), end_page (integer or null), and subchapters (an array of child objects with the same shape). Always include the "subchapters" key (use an empty array when there are none). Preserve nesting depth. Do not include any extra text outside the JSON.`;
              tocTextOrOutline = `OUTLINE_JSON:${outlineJson.slice(0,250000)}`;
            } catch (outlineErr) {
              console.error('[RagSetup] JSON-assisted: failed to resolve PDF outline', outlineErr);
              setError('Failed to load PDF outline for JSON-assisted mapping.');
              setIsProcessing(false);
              return;
            }
          } else {
            try {
              tocTextOrOutline = await extractPdfTextRange(file, s, e);
            } catch (errText) {
              console.error('[RagSetup] JSON-assisted: failed to extract TOC text', errText);
              setError('Failed to extract TOC pages text for JSON-assisted mapping.');
              setIsProcessing(false);
              return;
            }
          }

          const content: ContentListUnion = [prompt, `JSON:${jsonText}`, tocTextOrOutline.slice ? tocTextOrOutline.slice(0, 250000) : tocTextOrOutline];
          try {
            console.log('[RagSetup] JSON-assisted (text mode): sending content to Gemini (JSON + TOC text)');
            const genRespText: any = await ai.models.generateContent({ model: 'gemini-2.5-flash', contents: content as any });
            const outText = genRespText?.text ?? '';
            const jsonStart = outText.indexOf('[');
            const jsonEnd = outText.lastIndexOf(']');
            const jsonStr = jsonStart >= 0 && jsonEnd >= 0 ? outText.slice(jsonStart, jsonEnd + 1) : outText;
            const parsed = JSON.parse(jsonStr);
            function conv(it: any): ChapterRow {
              const name = (it?.name ?? '').toString();
              const aStart = toIntOrNull(it?.start_page ?? it?.start);
              const aEnd = toIntOrNull(it?.end_page ?? it?.end);
              const rawSubs = it?.subchapters ?? it?.sub_chapters ?? it?.children ?? [];
              const subs = Array.isArray(rawSubs) && rawSubs.length > 0 ? rawSubs.map((s: any) => conv(s)) : undefined;
              return { name, printedStart: null, printedEnd: null, alignedStart: aStart, alignedEnd: aEnd, subchapters: subs } as ChapterRow;
            }
            const rows = Array.isArray(parsed) ? parsed.map((it: any) => conv(it)) : [];
            setChapters(rows);
            console.log('[RagSetup] JSON-assisted (text mode): parsed mapping, chapters count=', rows.length);
          } catch (genErrText: any) {
            console.error('[RagSetup] JSON-assisted (text mode): error calling Gemini or parsing result', genErrText);
            setError('Failed to run JSON-assisted mapping via Gemini (text mode).');
            setChapters([]);
          }
          setIsProcessing(false);
          return;
        }

        // image mode: load entire PDF (to ensure page bounds) and split only the requested TOC range
        let pdfAll: any = null;
        try {
          pdfAll = await loadPdfDocument(file);
        } catch (e) {
          setError('Failed to load PDF for JSON-assisted mapping.');
          setIsProcessing(false);
          return;
        }
        const totalPages = Number(pdfAll.numPages) || 0;
        if (totalPages <= 0) {
          setError('PDF contains no pages.');
          setIsProcessing(false);
          try { await pdfAll.destroy?.(); } catch {}
          return;
        }

        const parts: { start: number; end: number; name: string }[] = [];
        // Only create parts covering the TOC range (s..e) when in image mode
        const startPage = Math.max(1, Math.min(s, totalPages));
        const endPage = Math.max(startPage, Math.min(e, totalPages));
        for (let p = startPage; p <= endPage; p += per) {
          const start = p;
          const end = Math.min(endPage, p + per - 1);
          parts.push({ start, end, name: `pages_${start}-${end}.pdf` });
        }
        console.log('[RagSetup] JSON-assisted (image mode): created parts metadata count=', parts.length, 'pagesPerUpload=', per, 'startPage=', startPage, 'endPage=', endPage);

        const withBlobs: Array<{ start: number; end: number; blob: Blob; name: string }> = [];
        for (const part of parts) {
          console.log('[RagSetup] JSON-assisted: creating blob for part', part.start, part.end);
          const blob = await createPdfForRange(file, part.start, part.end);
          withBlobs.push({ start: part.start, end: part.end, blob, name: part.name });
        }

        console.log('[RagSetup] JSON-assisted: created', withBlobs.length, 'blobs; registering uploadedFiles entries');
        setUploadedFiles(withBlobs.map((b) => ({ name: b.name, state: 'PENDING', blob: b.blob, start: b.start, end: b.end })));

        const entries = withBlobs.map((b) => ({ name: b.name, blob: b.blob, start: b.start, end: b.end }));
        console.log('[RagSetup] JSON-assisted: uploading', entries.length, 'entries to Gemini (concurrency=', uploadConcurrency, ')');
        const processedFiles = await uploadFilesWithConcurrency(ai, entries, uploadConcurrency);
        console.log('[RagSetup] JSON-assisted: upload completed; processedFiles summary=', processedFiles.map((p: any, i: number) => ({ name: withBlobs[i].name, uri: p?.uri, mimeType: p?.mimeType })));

        // Build prompt. Include the JSON text inline so Gemini knows the structure to map.
        let meta = `JSON:${jsonText}\nPARTS:${withBlobs.map((b) => `${b.name}:${b.start}-${b.end}`).join(';')}`;
        let prompt = `You are given a JSON document that lists chapters and optional subchapters for a textbook. The JSON follows nested objects like {name: \"...\", subchapters: [{name: \"...\"}]}. Using the uploaded PDF parts (which cover the TOC range, named and annotated in the PARTS metadata), determine for each chapter and subchapter the PDF page number(s) where they appear. Return ONLY a JSON array mirroring the input structure but with numeric fields start_page and end_page for each chapter and subchapter. For example: [{\"name\":\"Unit 1\",\"start_page\":12,\"end_page\":34,\"subchapters\":[{\"name\":\"Section A\",\"start_page\":12,\"end_page\":15}]}]. Do not include any extra text outside the JSON.`;
        // If user requested sending bookmark tree, include OUTLINE_JSON instead of PARTS
        if (sendBookmarkTree) {
          try {
            const outline = await getPdfOutlineTree(pdfAll);
            const outlineJson = JSON.stringify(outline || []);
            meta = `JSON:${jsonText}\nOUTLINE_JSON:${outlineJson.slice(0,250000)}`;
            prompt = `You are given a JSON document that lists chapters and optional subchapters for a textbook and a PDF bookmark (outline) JSON. The bookmark JSON represents nested children using the "items" or "children" fields — treat those as subchapters under their parent chapter. Using the outline JSON, determine for each TOP-LEVEL chapter and for each nested subchapter the PDF page number(s) where they appear. Return ONLY a JSON array mirroring the input structure where each object uses the exact keys: name (string), start_page (integer or null), end_page (integer or null), and subchapters (an array of child objects with the same shape). Always include the "subchapters" key (empty array if none). Preserve nesting depth and do not add extra commentary or text outside the JSON.`;
          } catch (outlineErr) {
            console.error('[RagSetup] JSON-assisted: failed to resolve PDF outline', outlineErr);
            setError('Failed to resolve PDF outline for JSON-assisted mapping.');
            try { await pdfAll.destroy?.(); } catch {}
            setIsProcessing(false);
            return;
          }
        }

        const content: ContentListUnion = [prompt + '\n' + meta];
        for (let i = 0; i < processedFiles.length; i++) {
          const pf = processedFiles[i];
          if (pf?.uri) {
            const mime = pf?.mimeType || 'application/pdf';
            content.push(createPartFromUri(pf.uri, mime));
          }
        }

        let genResp: any = null;
        try {
          console.log('[RagSetup] JSON-assisted: sending content to Gemini (generateContent) — parts count:', content.length - 1);
          genResp = await ai.models.generateContent({ model: 'gemini-2.5-flash', contents: content as any });
          console.log('[RagSetup] JSON-assisted: received response from Gemini');
        } catch (genErr: any) {
          console.error('Gemini generate error (JSON-assisted mode)', genErr);
          setError(`Failed to contact Gemini API for JSON-assisted mode: ${genErr?.message || String(genErr)}`);
          setIsProcessing(false);
          try { await pdfAll.destroy?.(); } catch {}
          return;
        }

        const out = genResp?.text ?? '';
        try {
          const jsonStart = out.indexOf('[');
          const jsonEnd = out.lastIndexOf(']');
          const jsonStr = jsonStart >= 0 && jsonEnd >= 0 ? out.slice(jsonStart, jsonEnd + 1) : out;
          const parsed = JSON.parse(jsonStr);

          // Convert parsed JSON (assumed to include start_page/end_page and nested subchapters)
          function conv(it: any): ChapterRow {
            const name = (it?.name ?? '').toString();
            const aStart = toIntOrNull(it?.start_page ?? it?.start);
            const aEnd = toIntOrNull(it?.end_page ?? it?.end);
            const rawSubs = it?.subchapters ?? it?.sub_chapters ?? it?.children ?? [];
            const subs = Array.isArray(rawSubs) && rawSubs.length > 0 ? rawSubs.map((s: any) => conv(s)) : undefined;
            return { name, printedStart: null, printedEnd: null, alignedStart: aStart, alignedEnd: aEnd, subchapters: subs } as ChapterRow;
          }

          const rows = Array.isArray(parsed) ? parsed.map((it: any) => conv(it)) : [];
          setChapters(rows);
          console.log('Gemini JSON-assisted mapping result:', parsed);
        } catch (parseErr) {
          console.error('Failed to parse Gemini JSON-assisted output', out, parseErr);
          setChapters([]);
        }

        try { await pdfAll.destroy?.(); } catch {}
        setIsProcessing(false);
        return;
      }

      const prompt = `You are given the Table of Contents of a textbook extracted from pages ${s}-${e}. Extract chapter entries and output ONLY JSON array with objects like this: [{"name": "Unit-1 health", "start=page": "12", "end-age": "123"}]. Use the exact keys: name, start=page, end-age. Do not include any extra text outside the JSON. If page ranges are not explicit, infer sensibly or leave fields empty strings. the end page is 1-the next chapter start page. do not include subchapters`;

      if (!useImageMode) {
        // 1) Extract text from the specified pages OR send bookmark tree JSON if requested
        console.log('[RagSetup] non-image TOC mode: processing TOC pages', s, e, 'sendBookmarkTree=', sendBookmarkTree);
        let response: any = null;
        if (sendBookmarkTree) {
          // Load PDF and resolve outline/bookmark tree
          let pdfDoc: any = null;
          try {
            pdfDoc = await loadPdfDocument(file);
            const outline = await getPdfOutlineTree(pdfDoc);
            try { await pdfDoc.destroy?.(); } catch {}
            const outlineJson = JSON.stringify(outline);
            const outlinePrompt = `You are given a PDF outline (bookmark) tree in JSON format. Using this outline, produce a Table of Contents JSON array with objects like [{"name":"Unit 1","start=page":12,"end-age":34}] where start=page and end-age are PDF page numbers. Return ONLY the JSON array and use the exact keys name, start=page, end-age.`;
            try {
              console.log('[RagSetup] non-image TOC mode: sending outline JSON to Gemini (truncated)');
              response = await ai.models.generateContent({ model: 'gemini-2.5-flash', contents: `${outlinePrompt}\n\nOUTLINE_JSON:${outlineJson.slice(0,250000)}` });
              console.log('[RagSetup] non-image TOC mode: received response from Gemini (outline)');
            } catch (genErr: any) {
              console.error('Gemini generate error (outline)', genErr);
              const msg = (genErr && (genErr.message || String(genErr))) || 'Failed to contact Gemini API';
              setError(`Failed to contact Gemini API: ${msg}.`);
              setIsProcessing(false);
              return;
            }
          } catch (errOutline: any) {
            console.error('Failed to load PDF or outline', errOutline);
            setError('Failed to load PDF outline for bookmark sending.');
            setIsProcessing(false);
            try { await pdfDoc?.destroy?.(); } catch {}
            return;
          }
        } else {
          // default: extract visible text for TOC pages and send to Gemini
          console.log('[RagSetup] non-image TOC mode: extracting text from PDF pages', s, e);
          const text = await extractPdfTextRange(file, s, e);
          try {
            console.log('[RagSetup] non-image TOC mode: sending prompt+text to Gemini (truncated)');
            response = await ai.models.generateContent({ model: 'gemini-2.5-flash', contents: `${prompt}\n\n${text.slice(0, 250000)}` });
            console.log('[RagSetup] non-image TOC mode: received response from Gemini');
          } catch (genErr: any) {
            console.error('Gemini generate error', genErr);
            const msg = (genErr && (genErr.message || String(genErr))) || 'Failed to contact Gemini API';
            setError(`Failed to contact Gemini API: ${msg}. Check network/DNS, your API key, or use the server-side proxy if available.`);
            setIsProcessing(false);
            return;
          }
        }
        const out = response?.text ?? '';
        // Try to parse JSON; if fails, log raw
        try {
          const jsonStart = out.indexOf('[');
          const jsonEnd = out.lastIndexOf(']');
          const jsonStr = jsonStart >= 0 && jsonEnd >= 0 ? out.slice(jsonStart, jsonEnd + 1) : out;
          const parsed = JSON.parse(jsonStr);
          const base = parseInt(alignBaseInput, 10);
          const rows = parseChaptersFromOutput(parsed, Number.isFinite(base) && base > 0 ? base : undefined);
          setChapters(rows);
          console.log('Gemini chapter JSON:', parsed);
        } catch (parseErr) {
          setChapters([]);
          console.log('Gemini raw output (not valid JSON):', out);
        }
      } else {
        // Image mode: create PDFs for page ranges then upload and ask Gemini to extract chapters from the files
        const per = Math.max(1, parseInt(pagesPerUpload || '4', 10));
        const parts: { start: number; end: number; blob?: Blob; name?: string }[] = [];
        for (let p = s; p <= e; p += per) {
          const partStart = p;
          const partEnd = Math.min(e, p + per - 1);
          parts.push({ start: partStart, end: partEnd, name: `toc_${partStart}-${partEnd}.pdf` });
        }
        console.log('[RagSetup] image mode: created parts metadata count=', parts.length, 'pagesPerUpload=', per);
        // create blobs for each part
        const withBlobs = [] as Array<{ start: number; end: number; blob: Blob; name: string }>;
        for (const part of parts) {
          console.log('[RagSetup] image mode: creating PDF blob for', part.start, part.end);
          const blob = await createPdfForRange(file, part.start, part.end);
          withBlobs.push({ start: part.start, end: part.end, blob, name: part.name! });
        }

        console.log('[RagSetup] image mode: created blobs count=', withBlobs.length);

        // reset upload status and keep blobs for retries
        setUploadedFiles(withBlobs.map((b) => ({ name: b.name, state: 'PENDING', blob: b.blob, start: b.start, end: b.end })));

        // upload with concurrency
        const entries = withBlobs.map((b) => ({ name: b.name, blob: b.blob, start: b.start, end: b.end }));
  console.log('[RagSetup] image mode: uploading', entries.length, 'entries to Gemini (concurrency=', uploadConcurrency, ')');
  const processedFiles = await uploadFilesWithConcurrency(ai, entries, uploadConcurrency);
  console.log('[RagSetup] image mode: upload completed; processedFiles summary=', processedFiles.map((p: any, i: number) => ({ name: withBlobs[i].name, uri: p?.uri, mimeType: p?.mimeType })));

        // Build content list: prompt + parts (as file parts via createPartFromUri)
        const content: ContentListUnion = [prompt];
        for (let i = 0; i < processedFiles.length; i++) {
          const pf = processedFiles[i];
          if (pf?.uri) {
            const mime = pf?.mimeType || 'application/pdf';
            content.push(createPartFromUri(pf.uri, mime));
          }
        }

        let response: any = null;
        try {
          console.log('[RagSetup] image mode: sending generateContent to Gemini (file parts count=', content.length - 1, ')');
          response = await ai.models.generateContent({ model: 'gemini-2.5-flash', contents: content as any });
          console.log('[RagSetup] image mode: received response from Gemini');
        } catch (genErr: any) {
          console.error('Gemini generate error (file mode)', genErr);
          setError(`Failed to contact Gemini API for file mode: ${genErr?.message || String(genErr)}`);
          setIsProcessing(false);
          return;
        }

        const out = response?.text ?? '';
        try {
          const jsonStart = out.indexOf('[');
          const jsonEnd = out.lastIndexOf(']');
          const jsonStr = jsonStart >= 0 && jsonEnd >= 0 ? out.slice(jsonStart, jsonEnd + 1) : out;
          const parsed = JSON.parse(jsonStr);
          const base = parseInt(alignBaseInput, 10);
          const rows = parseChaptersFromOutput(parsed, Number.isFinite(base) && base > 0 ? base : undefined);
          setChapters(rows);
          console.log('Gemini chapter JSON (file mode):', parsed);
        } catch (parseErr) {
          setChapters([]);
          console.log('Gemini raw output (not valid JSON) (file mode):', out);
        }
      }
    } catch (err: any) {
      console.error('RAG Setup processing error:', err);
      setError(err?.message || 'Failed to process. Check console for details.');
    } finally {
      setIsProcessing(false);
    }
  };

  // After alignment: chunk chapters by aligned PDF page ranges, with progress
  const handleChunkChapters = async () => {
    try {
      setError(null);
      if (!file) {
        setError('Please choose a PDF file first.');
        return;
      }

      // Load PDF to get total page count and validate aligned ranges
      const pdf = await loadPdfDocument(file);
      const totalPdfPages = Number(pdf.numPages) || 0;

      const alignmentIssues = (chapters || []).map((c, idx) => ({
        idx,
        name: c.name || `Chapter ${idx + 1}`,
        alignedStart: c.alignedStart,
        alignedEnd: c.alignedEnd,
      })).filter(it => it.alignedStart == null || it.alignedEnd == null || it.alignedStart < 1 || (totalPdfPages > 0 && it.alignedEnd > totalPdfPages) || it.alignedEnd < it.alignedStart);

      if (alignmentIssues.length > 0) {
        const list = alignmentIssues.map(i => `${i.name} (start: ${i.alignedStart ?? '-'}, end: ${i.alignedEnd ?? '-'})`).join('; ');
        setError(`Chapter alignment issues detected. Please fix aligned ranges before chunking. Problems: ${list}`);
        try { await pdf.destroy?.(); } catch {}
        return;
      }

      // In university mode, prefer chunking by subchapters when Gemini provided them
      let ranges: Array<any> = [];
      if (institutionMode === 'university') {
        // For each parsed chapter, if it has subchapters use their aligned ranges
        for (let idx = 0; idx < (chapters || []).length; idx++) {
          const c = chapters[idx];
          if (Array.isArray(c.subchapters) && c.subchapters.length > 0) {
            for (let sidx = 0; sidx < c.subchapters.length; sidx++) {
              const sc = c.subchapters[sidx];
              const start = Number(sc.alignedStart || 0);
              const end = Number(sc.alignedEnd || 0);
              if (Number.isFinite(start) && Number.isFinite(end) && start > 0 && end >= start) {
                ranges.push({ parentIdx: idx, subIdx: sidx, name: `${c.name} / ${sc.name}`, start, end, parentName: c.name, subName: sc.name });
              }
            }
          } else {
            // fallback to whole chapter if no subchapters
            const start = Number(c.alignedStart || 0);
            const end = Number(c.alignedEnd || 0);
            if (Number.isFinite(start) && Number.isFinite(end) && start > 0 && end >= start) {
              ranges.push({ parentIdx: idx, subIdx: null, name: c.name, start, end, parentName: c.name, subName: null });
            }
          }
        }
      } else {
        ranges = (chapters || []).map((c, idx) => ({
          idx,
          name: c.name || `Chapter ${idx + 1}`,
          start: Number(c.alignedStart || 0),
          end: Number(c.alignedEnd || 0),
        })).filter(r => Number.isFinite(r.start) && Number.isFinite(r.end) && r.start > 0 && r.end >= r.start);
      }

      if (ranges.length === 0) {
        setError('No chapters have valid aligned page ranges. Please set alignment first.');
        try { await pdf.destroy?.(); } catch {}
        return;
      }

      const totalPages = ranges.reduce((acc, r) => acc + (r.end - r.start + 1), 0);
      setChunkSummaries([]);
      setChunkItems([]);
      setIsChunking(true);
      setChunkProgress({ completed: 0, total: totalPages, label: 'Preparing…' });

      let done = 0;
      const summaries: { name: string; start: number; end: number; chunks: number }[] = [];
      const items: ChunkItem[] = [];

      for (let ri = 0; ri < ranges.length; ri++) {
        const r = ranges[ri];
        let chapterCount = 0;
        for (let p = r.start; p <= r.end; p++) {
          const text = await extractPageText(pdf, p);
          const norm = (text || '').trim();
          if (norm.length > 0) {
            // For university mode we keep parent/sub indices on the chunk item for later saving
            const chunk: ChunkItem = {
              chapterIndex: ri,
              chapterName: r.name,
              page: p,
              text: norm,
              parentChapterIndex: r.parentIdx ?? r.idx ?? null,
              subchapterIndex: (typeof r.subIdx !== 'undefined') ? r.subIdx : null,
              parentChapterTitle: r.parentName ?? r.name,
              subchapterTitle: r.subName ?? null,
            };
            items.push(chunk);
            chapterCount += 1;
          }
          done += 1;
          setChunkProgress({ completed: done, total: totalPages, label: `${r.name}: page ${p} of ${r.end}` });
        }
        summaries.push({ name: r.name, start: r.start, end: r.end, chunks: chapterCount });
      }

      try { await pdf.destroy?.(); } catch {}
      setChunkSummaries(summaries);
      setChunkItems(items);
    } catch (e: any) {
      console.error('Chunking error:', e);
      setError(e?.message || 'Failed to chunk chapters.');
    } finally {
      setIsChunking(false);
    }
  };

  const EMBEDDING_MODEL = 'models/gemini-embedding-001';

  const handleEmbedChunks = async () => {
    try {
      setError(null);
      if (!apiKey.trim()) {
        console.warn('[Embed] Missing API key');
        setError('Please enter your Gemini API key.');
        return;
      }
      if (!chunkItems || chunkItems.length === 0) {
        console.warn('[Embed] No chunks to embed');
        setError('No chunks to embed. Please run chunking first.');
        return;
      }
      setIsEmbedding(true);
      setEmbedSummaries([]);
      setEmbedProgress({ completed: 0, total: chunkItems.length, label: 'Preparing…' });
      console.log(`[Embed] Starting embedding: total chunks=${chunkItems.length}`);
      const clientOptions: any = { apiKey: apiKey.trim() };
      if (useStaging) {
        clientOptions.httpOptions = { baseUrl: 'https://autopush-generativelanguage.sandbox.googleapis.com' };
      }
      console.log('[Embed] Client initialized', { useStaging });
      const ai = new GoogleGenAI(clientOptions);

      // Helper: split array into groups of 10
      const groups: ChunkItem[][] = [];
      for (let i = 0; i < chunkItems.length; i += 10) {
        groups.push(chunkItems.slice(i, i + 10));
      }
      console.log(`[Embed] Grouped into ${groups.length} batches of up to 10 items`);

  let completed = 0;
  const perChapter = new Map<string, number>();
  const details = new Map<string, Array<{ page: number; text: string; raw: any }>>();

      for (let gi = 0; gi < groups.length; gi++) {
        const group = groups[gi];
        try {
          // Try direct embedContent for the group (example usage: contents as array of strings)
          setEmbedProgress({ completed, total: chunkItems.length, label: `Embedding group ${gi + 1}/${groups.length}…` });
          const contentsArr = group.map((ci) => ci.text);
          const resp: any = await (ai as any).models.embedContent({
            model: EMBEDDING_MODEL,
            contents: contentsArr,
          });

          // If we get a successful response, count the items (best-effort)
          const produced = Array.isArray(resp?.responses) ? resp.responses.length : group.length;
          // collect raw response items if present
          const responsesArr = resp?.responses ?? resp?.embeddings ?? resp?.data ?? resp?.items ?? resp?.outputs ?? [];
          for (let i = 0; i < group.length; i++) {
            const ci = group[i];
            perChapter.set(ci.chapterName, (perChapter.get(ci.chapterName) || 0) + 1);
            const rawItem = responsesArr[i] ?? null;
            const rawPreview = normalizeEmbeddingItem(rawItem);
            const arr = details.get(ci.chapterName) ?? [];
            arr.push({ page: ci.page, text: ci.text, raw: rawPreview });
            details.set(ci.chapterName, arr);
          }
          completed += produced;
          setEmbedProgress({ completed, total: chunkItems.length, label: `Completed group ${gi + 1}/${groups.length}` });
          // persist interim details to state so UI can preview during long runs
          setEmbedDetails(Object.fromEntries(Array.from(details.entries())));
          console.log(`[Embed] embedContent succeeded for group ${gi + 1}/${groups.length}: items=${group.length}`);
        } catch (err: any) {
          console.warn(`[Embed] embedContent failed for group ${gi + 1}/${groups.length}:`, err);
          setEmbedProgress({ completed, total: chunkItems.length, label: `Group ${gi + 1} failed; falling back small groups…` });

          // Fallback: split group into small batches (2-5) and call embedContent for each small group
          const FALLBACK_BATCH_SIZE = 4;
          const smallGroups: ChunkItem[][] = [];
          for (let si = 0; si < group.length; si += FALLBACK_BATCH_SIZE) {
            smallGroups.push(group.slice(si, si + FALLBACK_BATCH_SIZE));
          }

          for (let sgi = 0; sgi < smallGroups.length; sgi++) {
            const smallGroup = smallGroups[sgi];
            try {
              const smallContents = smallGroup.map((ci) => ci.text);
              const sresp: any = await (ai as any).models.embedContent({ model: EMBEDDING_MODEL, contents: smallContents });
              const count = Array.isArray(sresp?.responses) ? sresp.responses.length : smallGroup.length;
              const sResponsesArr = sresp?.responses ?? sresp?.embeddings ?? sresp?.data ?? sresp?.items ?? sresp?.outputs ?? [];
              for (let i = 0; i < smallGroup.length; i++) {
                const ci = smallGroup[i];
                perChapter.set(ci.chapterName, (perChapter.get(ci.chapterName) || 0) + 1);
                const rawItem = sResponsesArr[i] ?? null;
                const rawPreview = normalizeEmbeddingItem(rawItem);
                const arr = details.get(ci.chapterName) ?? [];
                arr.push({ page: ci.page, text: ci.text, raw: rawPreview });
                details.set(ci.chapterName, arr);
              }
              completed += count;
              // persist interim details after each small batch
              setEmbedDetails(Object.fromEntries(Array.from(details.entries())));
              console.log(`[Embed] fallback embedContent succeeded small batch ${gi + 1}.${sgi + 1}: items=${smallGroup.length}`);
            } catch (se) {
              console.warn('Embed fallback small group failed', gi + 1, sgi + 1, se);
            }
            setEmbedProgress({ completed, total: chunkItems.length, label: `Fallback processed ${sgi + 1}/${smallGroups.length}` });
          }
        }
      }

      // Finalize summary
      const summaryArr = Array.from(perChapter.entries()).map(([chapterName, count]) => ({ chapterName, count }));
      setEmbedSummaries(summaryArr);
      setEmbedProgress({ completed, total: chunkItems.length, label: 'Embedding completed' });
      // mark a flag so UI shows Finish button
      setTimeout(() => {
        // delay a tick to allow UI to update
      }, 0);
    } catch (e: any) {
      console.error('Embedding error:', e);
      setError(e?.message || 'Failed to embed chunks.');
    } finally {
      setIsEmbedding(false);
    }
  };

  // Save book and chunks to backend (Finish)
  const handleSaveBook = async () => {
    if (!bookName || !chunkItems || chunkItems.length === 0) {
      setError('No book or chunks to save');
      return;
    }
    try {
      const payload: any = { title: bookName, author: '', chapters: [] };
      // Build chapters array from chunkItems grouping by chapter/subchapter when in university mode
      const byGroup: Record<string, any[]> = {};
      const groupMeta: Record<string, any> = {};
      for (const ci of chunkItems) {
        const embDetails = embedDetails?.[ci.chapterName] ?? [];
        // try to find embedding for this page/text
        const found = embDetails.find((d:any) => d.page === ci.page && d.text && d.text.startsWith((ci.text || '').slice(0,50)));
        const embedding = found?.raw?.embedding_vector ?? found?.raw?.sample ?? null;
  // Determine grouping key: for university use parentIdx_subIdx, otherwise use chapterIndex
  const parentIdx = (ci as any).parentChapterIndex;
  const subIdx = (ci as any).subchapterIndex;
  const groupKey = (institutionMode === 'university' && parentIdx != null) ? `p${parentIdx}_s${subIdx ?? 'null'}` : `c${ci.chapterIndex}`;
  byGroup[groupKey] = byGroup[groupKey] || [];

        // Compute the original printed page (page_no) to persist.
        // Prefer per-(sub)chapter mapping when available: printed = printedStart + (alignedPage - alignedStart)
        // Otherwise fallback to global alignment base (alignBaseInput): printed = alignedPage - base + 1
        // If neither is available, save the aligned PDF page as a fallback.
        let printedPage: number | null = null;
        try {
          const alignedPage = Number(ci.page);
          // If university mode and we have parent/sub indices, prefer their mapping
          if (institutionMode === 'university' && parentIdx != null) {
            const parent = (chapters || [])[parentIdx] as any;
            const sub = (parent?.subchapters && typeof subIdx === 'number') ? parent.subchapters[subIdx] : null;
            const alignedStart = sub?.alignedStart ?? parent?.alignedStart;
            const printedStart = sub?.printedStart ?? parent?.printedStart;
            if (alignedStart != null && printedStart != null) {
              printedPage = Number(printedStart) + (alignedPage - Number(alignedStart));
            }
          }
          if (printedPage == null) {
            // fallback to chapter-level mapping
            if (ci.chapterIndex != null) {
              const ch = (chapters || [])[ci.chapterIndex] as any;
              if (ch && Number.isFinite(ch?.alignedStart) && Number.isFinite(ch?.printedStart)) {
                printedPage = Number(ch.printedStart) + (alignedPage - Number(ch.alignedStart));
              }
            }
          }
          if (printedPage == null) {
            const baseNum = parseInt(alignBaseInput || '', 10);
            if (Number.isFinite(baseNum) && baseNum > 0) {
              printedPage = alignedPage - baseNum + 1;
            } else {
              printedPage = alignedPage;
            }
          }
        } catch (e) {
          printedPage = ci.page;
        }
        if (!Number.isFinite(printedPage as number) || printedPage == null) printedPage = ci.page;
        if ((printedPage as number) < 1) printedPage = 1;

        byGroup[groupKey].push({ chunk_id: `${groupKey}-${ci.page}`, text: ci.text, embedding, page_no: printedPage });

        // record meta for this group (title, subchapter_title, start/end pages, api mapping)
        if (!groupMeta[groupKey]) {
          if (institutionMode === 'university' && parentIdx != null) {
            const parent = (chapters || [])[parentIdx] as any;
            const sub = (parent?.subchapters && typeof subIdx === 'number') ? parent.subchapters[subIdx] : null;
            const start = sub ? sub.alignedStart : parent?.alignedStart;
            const end = sub ? sub.alignedEnd : parent?.alignedEnd;
            const apiId = sub ? (subchapterMappings?.[`${parentIdx}_${subIdx}`] ?? null) : (chapterMappings[parentIdx] ?? null);
            groupMeta[groupKey] = { number: (parentIdx + 1), title: parent?.name ?? '', subchapter_title: sub?.name ?? null, start_page: start ?? null, end_page: end ?? null, api_chapter_id: apiId };
          } else {
            // school flow or fallback: group by chapterIndex
            const chIdx = ci.chapterIndex;
            const ch = (chapters || [])[chIdx] as any;
            groupMeta[groupKey] = { number: (chIdx + 1), title: ch?.name ?? '', subchapter_title: null, start_page: ch?.alignedStart ?? null, end_page: ch?.alignedEnd ?? null, api_chapter_id: chapterMappings[chIdx] ?? null };
          }
        }
      }

      // correlate groups with chapters state and build payload entries
      for (const [gk, chunks] of Object.entries(byGroup)) {
        const meta = groupMeta[gk] || {};
        payload.chapters.push({ number: meta.number || 0, title: meta.title || '', subchapter_title: meta.subchapter_title || null, start_page: meta.start_page || null, end_page: meta.end_page || null, api_chapter_id: meta.api_chapter_id || null, chunks });
      }

      // Add metadata linking: course (university) or grade/subject (school)
      if (institutionMode === 'university') {
        // prefer selectedCourse
        payload.course_id = selectedCourse;
      } else {
        payload.school_grade_id = selectedGrade;
        payload.school_subject_id = selectedSubject;
      }

      const data = await request('/api/textbook/save_book/', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload) });
      console.log('Save result', data);
      console.log('Save result', data);
      // show success (could navigate)
      setError(null);
      setCurrentStep(1);
    } catch (e:any) {
      console.error('Save failed', e);
      setError(e?.message || 'Save failed');
    }
  };


  if (role !== 'superuser') {
    return (
      <div className="rounded border border-red-300 bg-red-50 p-4 text-red-700">
        403 - Not authorized
      </div>
    );
  }

  return (
    <div className="w-full rounded-2xl bg-gradient-to-br from-teal-50/80 via-white to-white px-5 pt-6 pb-4 shadow-[0_10px_25px_rgba(0,0,0,0.05)]">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">RAG Setup</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step-by-step: upload textbook, preview & set TOC, then generate chapters and align pages.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left: Progress sidebar (DNB style) */}
        <div className="col-span-12 lg:col-span-4">
          <div className="rounded-2xl border border-teal-100 bg-teal-50/70 p-6">
              <div className="relative">
              {[1,2,3,4,5,6].map((n, idx) => {
                const labels: Record<number,string> = { 1: 'Select PDF', 2: 'Preview & TOC', 3: 'Chapters & Align', 4: 'Map Chapters', 5: 'Chunk & Summary', 6: 'Embed Chunks' };
                const active = currentStep === n;
                const done = currentStep > n;
                return (
                  <div key={n} className="relative pl-12 pb-8 last:pb-0">
                    {/* connector line */}
                    {idx < 5 && (
                      <div className="absolute left-5 top-8 h-[calc(100%-2rem)] w-0.5 bg-gradient-to-b from-teal-300/60 via-teal-300/40 to-transparent" />
                    )}
                    {/* circle */}
                    <div className={`absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold shadow-sm ring-4 ring-white ${done ? 'bg-emerald-500 text-white' : active ? 'bg-white text-teal-600 border-2 border-teal-400' : 'bg-white text-teal-400 border-2 border-teal-200'}`}>
                      {done ? '✓' : n}
                    </div>
                    {/* label */}
                    <div className={`text-[15px] ${active ? 'font-semibold text-teal-700' : 'text-gray-600'}`}>{labels[n]}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right: Step content */}
        <div className="col-span-12 lg:col-span-8">
          {currentStep === 1 && (
            <div className="rounded-2xl border border-gray-200/70 bg-white p-6 shadow-sm">
              <h4 className="mb-3 text-base font-semibold">1. Select PDF</h4>
              <div className="flex flex-col gap-3">
                <div>
                  <Label htmlFor="pdfInput" value="Select PDF file" />
                  <input
                    id="pdfInput"
                    aria-label="Select PDF file"
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileChange}
                    className="mt-1 block w-full text-sm text-gray-700 file:mr-4 file:rounded file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-semibold hover:file:bg-gray-200 dark:text-gray-200 dark:file:bg-gray-700 dark:hover:file:bg-gray-600"
                  />
                </div>
                <div>
                  <Label htmlFor="bookName" value="Book name" />
                  <TextInput id="bookName" value={bookName} onChange={(e) => setBookName(e.target.value)} placeholder="e.g., Biology Grade 7" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label value="Institution mode" />
                    <div className="mt-1 flex gap-2">
                      <button
                        type="button"
                        aria-label="Select School mode"
                        onClick={() => setInstitutionMode('school')}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${institutionMode === 'school' ? 'bg-teal-600 text-white shadow-sm' : 'bg-white text-gray-700 border border-gray-200'}`}>
                        School
                      </button>
                      <button
                        type="button"
                        aria-label="Select University mode"
                        onClick={() => setInstitutionMode('university')}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${institutionMode === 'university' ? 'bg-teal-600 text-white shadow-sm' : 'bg-white text-gray-700 border border-gray-200'}`}>
                        University / College
                      </button>
                    </div>
                  </div>
                  <div />
                </div>

                {institutionMode === 'school' && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <Label htmlFor="gradeSelect" value="Grade" />
                      <select id="gradeSelect" aria-label="Grade" className="mt-1 block w-full rounded border-gray-200 p-2 text-sm" value={selectedGrade ?? ''} onChange={(e) => { const v = e.target.value ? parseInt(e.target.value,10) : null; setSelectedGrade(v); setSelectedSubject(null); }}>
                        <option value="">-- choose grade --</option>
                        {grades.map(g => (<option key={g.id} value={g.id}>{g.grade_name ?? g.name ?? g.id}</option>))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="subjectSelect" value="Subject" />
                      <select id="subjectSelect" aria-label="Subject" className="mt-1 block w-full rounded border-gray-200 p-2 text-sm" value={selectedSubject ?? ''} onChange={(e) => setSelectedSubject(e.target.value ? parseInt(e.target.value,10) : null)} disabled={!selectedGrade}>
                        <option value="">-- choose subject --</option>
                        {subjects.map(s => (<option key={s.id} value={s.id}>{s.subject_name ?? s.name ?? s.id}</option>))}
                      </select>
                    </div>
                  </div>
                )}

                  {/* DEBUG: show raw chapters JSON for troubleshooting UI updates */}
                  <div className="mt-4">
                    <details className="text-xs text-gray-500">
                      <summary className="cursor-pointer">Debug: show chapters JSON</summary>
                      <pre className="mt-2 max-h-64 overflow-auto rounded bg-gray-50 p-3 text-[12px]">{JSON.stringify(chapters, null, 2)}</pre>
                    </details>
                  </div>

                {institutionMode === 'university' && (
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div>
                      <Label htmlFor="fieldSelect" value="Field of Study" />
                      <select id="fieldSelect" aria-label="Field of Study" title="Field of Study" className="mt-1 block w-full rounded border-gray-200 p-2 text-sm" value={selectedField ?? ''} onChange={(e) => setSelectedField(e.target.value ? parseInt(e.target.value,10) : null)} disabled={fieldsLoading}>
                        <option value="">-- choose field --</option>
                        {fields.map(f => (<option key={f.id} value={f.id}>{f.name}</option>))}
                      </select>
                      {fieldsLoading && <div className="mt-1 text-xs text-gray-500">Loading fields...</div>}
                      {!fieldsLoading && fields.length === 0 && <div className="mt-1 text-xs text-gray-500">No fields available</div>}
                    </div>
                    <div>
                      <Label htmlFor="yearSelect" value="Academic Year" />
                      <select id="yearSelect" aria-label="Academic Year" title="Academic Year" className="mt-1 block w-full rounded border-gray-200 p-2 text-sm" value={selectedAcademicYear ?? ''} onChange={(e) => setSelectedAcademicYear(e.target.value ? parseInt(e.target.value,10) : null)} disabled={!selectedField}>
                        <option value="">-- choose year --</option>
                        {academicYears.map(y => (<option key={y.id} value={y.id}>{y.name}</option>))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="courseSelect" value="Course" />
                      <select id="courseSelect" aria-label="Course" title="Course" className="mt-1 block w-full rounded border-gray-200 p-2 text-sm" value={selectedCourse ?? ''} onChange={(e) => setSelectedCourse(e.target.value ? parseInt(e.target.value,10) : null)} disabled={!selectedAcademicYear}>
                        <option value="">-- choose course --</option>
                        {courses.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
                      </select>
                    </div>
                  </div>
                )}
                {fileInfo && (
                  <div className="flex items-center justify-between rounded bg-gray-50 p-2 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                    <span>{fileInfo}</span>
                    <Button size="xs" color="gray" onClick={clearSelection}>Clear</Button>
                  </div>
                )}
                {error && (
                  <div className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-700">{error}</div>
                )}
                <div className="mt-2 flex justify-end">
                  <Button onClick={() => setCurrentStep(2)} disabled={!file || !bookName.trim() || (institutionMode === 'school' ? (!selectedGrade || !selectedSubject) : (!selectedField || !selectedAcademicYear || !selectedCourse))} className="rounded-full bg-teal-600 hover:bg-teal-700 text-white px-6 shadow-sm">Next</Button>
                </div>
              </div>

              <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="md:col-span-2">
                  <Label htmlFor="lastChapterEnd" value="Set last chapter end (PDF page)" />
                  <TextInput id="lastChapterEnd" type="number" min={1} value={lastChapterEndInput} onChange={(e) => setLastChapterEndInput(e.target.value)} placeholder="e.g., 400" />
                </div>
                <div className="flex items-end">
                  <Button onClick={setLastChapterEnd} className="rounded-full bg-white text-teal-700 border border-teal-300 hover:bg-teal-50 px-6">Set Last End</Button>
                </div>
              </div>

              {alignmentNotice && (
                <div className="mb-3 rounded border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-800">{alignmentNotice}</div>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div className="rounded-2xl border border-gray-200/70 bg-white p-6 shadow-sm">
              <h4 className="mb-3 text-base font-semibold">2. Preview & enter API key and TOC pages</h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="md:col-span-3">
                  <Label value="Gemini API Key" />
                  <div className="mt-1 flex items-center gap-3">
                    <div className="text-sm text-gray-600">{apiKey?.trim() ? 'API key configured' : 'No API key configured'}</div>
                    <Button size="xs" onClick={() => setShowApiKeyModal(true)}>Manage key</Button>
                  </div>
                  <label className="mt-2 inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={useImageMode} onChange={(e) => setUseImageMode(e.target.checked)} />
                    Use image/file upload mode for TOC extraction (send pages as PDF files)
                  </label>
                  {useImageMode && (
                    <div className="mt-2">
                      <Label htmlFor="pagesPerUpload" value="Pages per upload (PDF)" />
                      <TextInput id="pagesPerUpload" type="number" min={1} value={pagesPerUpload} onChange={(e) => setPagesPerUpload(e.target.value)} placeholder="e.g., 4" />
                    </div>
                  )}
                  {institutionMode === 'university' && (
                    <>
                      <label className="mt-2 inline-flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={useJsonMode} onChange={(e) => setUseJsonMode(e.target.checked)} />
                        Use JSON-assisted mapping (upload JSON of chapters/subchapters)
                      </label>
                      {useJsonMode && (
                        <div className="mt-2">
                          <Label htmlFor="jsonInput" value="Upload chapters JSON" />
                          <input id="jsonInput" aria-label="Upload chapters JSON file" type="file" accept="application/json,.json" onChange={(e) => { const f = e.target.files?.[0] || null; setJsonFile(f); }} className="mt-1 block w-full text-sm text-gray-700 file:mr-4 file:rounded file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-semibold hover:file:bg-gray-200" />
                          <div className="mt-1 text-xs text-gray-500">When enabled, the uploaded JSON will be sent to Gemini along with PDF page chunks to determine page numbers for chapters and subchapters.</div>
                        </div>
                      )}
                    </>
                  )}
                  <label className="mt-2 inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={useStaging} onChange={(e) => setUseStaging(e.target.checked)} />
                    Use Gemini sandbox (autopush) endpoint
                  </label>
                </div>
                <div>
                  <Label htmlFor="tocStart" value="TOC Start Page" />
                  <TextInput id="tocStart" type="number" min={1} value={tocStart} onChange={(e) => setTocStart(e.target.value)} placeholder="e.g., 1" />
                </div>
                <div>
                  <Label htmlFor="tocEnd" value="TOC End Page" />
                  <TextInput id="tocEnd" type="number" min={1} value={tocEnd} onChange={(e) => setTocEnd(e.target.value)} placeholder="e.g., 5" />
                </div>
                <div className="flex items-end">
                  <div className="inline-flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={sendBookmarkTree} onChange={(e) => setSendBookmarkTree(e.target.checked)} />
                      Send bookmark tree JSON to Gemini (instead of TOC text)
                    </label>
                    <Button size="sm" color="gray" onClick={async () => { await logBookmarkTree(); }} className="ml-2">Log bookmark tree</Button>
                  </div>
                </div>
              </div>

              {!previewUrl ? (
                <div className="mt-4 rounded border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  No file selected. Go back and choose a PDF.
                </div>
              ) : (
                <div className="mt-4 overflow-hidden rounded border border-gray-200 dark:border-gray-700">
                  <iframe title="PDF Preview" src={previewUrl} className="h-[60vh] w-full" />
                </div>
              )}

              {error && (
                <div className="mt-3 rounded border border-red-300 bg-red-50 p-2 text-sm text-red-700">{error}</div>
              )}

              <div className="mt-4 flex items-center justify-between">
                <Button onClick={() => setCurrentStep(1)} className="rounded-full bg-white text-teal-700 border border-teal-300 hover:bg-teal-50 px-6">Back</Button>
                <Button
                  className="rounded-full bg-teal-600 hover:bg-teal-700 text-white px-6 shadow-sm"
                  onClick={async () => {
                    const ok = await (async () => {
                      try {
                        if (!file) { setError('Please choose a PDF file first.'); return false; }
                        if (!tocStart || !tocEnd) { setError('Please enter TOC start and end pages.'); return false; }
                        await handleContinue();
                        return true;
                      } catch {
                        return false;
                      }
                    })();
                    if (ok) setCurrentStep(3);
                  }}
                  isProcessing={isProcessing}
                  disabled={isProcessing || !file || !tocStart || !tocEnd}
                >
                  Generate chapters
                </Button>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="rounded-2xl border border-gray-200/70 bg-white p-6 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-base font-semibold">3. Chapters & page alignment</h4>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={showStep3Preview} onChange={(e) => setShowStep3Preview(e.target.checked)} />
                  Show PDF preview at bottom
                </label>
              </div>
              <p className="mb-3 text-sm text-gray-600 dark:text-gray-300">Sometimes PDF page indices differ from printed page numbers. Enter the PDF page index where printed page 1 appears; we will align each chapter's printed start/end to real PDF pages.</p>

              <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="md:col-span-2">
                  <Label htmlFor="alignBase" value="PDF page where printed page 1 exists" />
                  <TextInput id="alignBase" type="number" min={1} value={alignBaseInput} onChange={(e) => setAlignBaseInput(e.target.value)} placeholder="e.g., 7" />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={async () => {
                      const b = parseInt(alignBaseInput, 10);
                      if (Number.isFinite(b) && b > 0) {
                        try {
                          await applyAlignment(b);
                          // show transient notice so user sees alignment applied without opening console
                          setAlignmentNotice('Alignment applied');
                          setTimeout(() => setAlignmentNotice(null), 3000);
                        } catch (e: any) {
                          console.error('Alignment failed', e);
                          setError(e?.message || 'Failed to apply alignment.');
                        }
                      } else setError('Please enter a valid PDF page number for alignment (>= 1).');
                    }}
                    className="rounded-full bg-white text-teal-700 border border-teal-300 hover:bg-teal-50 px-6"
                  >
                    Apply Alignment
                  </Button>
                </div>
              </div>

              <div className="mb-4 text-sm text-gray-600">
                Note: if the parsed chapters omit an explicit end for the final chapter, applying alignment will set that chapter's aligned end to the PDF's last page so it covers the remainder of the book.
              </div>

              {(!chapters || chapters.length === 0) ? (
                <div className="rounded border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  No chapters parsed yet. You can go back to adjust TOC pages and regenerate.
                </div>
              ) : (
                <div>
                  <h5 className="mb-2 font-semibold">Chapters</h5>
                  <div className="divide-y divide-gray-100 rounded border border-gray-100 dark:divide-gray-700 dark:border-gray-700">
                    {chapters.map((c, idx) => (
                      <div key={`${idx}-${forceRerender}`} className="grid grid-cols-1 gap-3 px-3 py-3 md:grid-cols-5">
                        <div className="md:col-span-2">
                          <div className="font-medium">{c.name || `Chapter ${idx + 1}`}</div>
                          <div className="mt-1 text-xs text-gray-500">Printed pages: {c.printedStart ?? '-'}{(c.printedStart!=null||c.printedEnd!=null)?' - ':''}{c.printedEnd ?? ''}</div>
                        </div>
                        <div>
                          <Label htmlFor={`alignedStart-${idx}`} value="Aligned Start (PDF page)" />
                          <div className="mt-1 rounded border border-gray-200 bg-gray-50 p-2 text-sm text-gray-700">{c.alignedStart != null ? String(c.alignedStart) : '-'}</div>
                        </div>
                        <div>
                          <Label htmlFor={`alignedEnd-${idx}`} value="Aligned End (PDF page)" />
                          <div className="mt-1 rounded border border-gray-200 bg-gray-50 p-2 text-sm text-gray-700">{c.alignedEnd != null ? String(c.alignedEnd) : '-'}</div>
                        </div>
                        {c.subchapters && c.subchapters.length > 0 && (
                          <div className="md:col-span-5 mt-2">
                            <div className="mt-3 text-sm font-medium">Subchapters</div>
                            <div className="mt-2 space-y-2">
                              {c.subchapters.map((sc, sidx) => (
                                <div key={`${idx}-${sidx}-${forceRerender}`} className="flex items-center justify-between rounded border border-gray-100 p-2 text-sm">
                                  <div className="truncate">{sc.name || `Subchapter ${sidx + 1}`}</div>
                                  <div className="text-xs text-gray-500">{sc.alignedStart ?? '-'} - {sc.alignedEnd ?? '-'}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {error && (
                <div className="mt-3 rounded border border-red-300 bg-red-50 p-2 text-sm text-red-700">{error}</div>
              )}

              <div className="mb-4 flex items-center justify-between">
                        <Button onClick={() => setCurrentStep(3)} className="rounded-full bg-white text-teal-700 border border-teal-300 hover:bg-teal-50 px-6">Back</Button>
                        <Button
                          onClick={() => { setCurrentStep(4); }}
                          disabled={!chapters || chapters.length === 0}
                          className="rounded-full bg-teal-600 hover:bg-teal-700 text-white px-6 shadow-sm"
                        >
                          Next: Map Chapters
                        </Button>
                      </div>

              {showStep3Preview && previewUrl && (
                <div className="mt-6 overflow-hidden rounded border border-gray-200 dark:border-gray-700">
                  <iframe title="PDF Preview (Step 3)" src={previewUrl} className="h-[60vh] w-full" />
                </div>
              )}
            </div>
          )}
          {currentStep === 4 && (
            <div className="rounded-2xl border border-gray-200/70 bg-white p-6 shadow-sm">
              <h4 className="mb-3 text-base font-semibold">4. Map Chapters</h4>

              <p className="mb-3 text-sm text-gray-600">Map the generated chapters to existing chapters in the database for the selected subject. You can auto-match by name or pick manually.</p>

              {(!chapters || chapters.length === 0) ? (
                <div className="rounded border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  No parsed chapters to map. Go back and generate chapters first.
                </div>
              ) : (
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm text-gray-600">DB chapters for subject: {dbChapters.length} found</div>
                      <div className="flex items-center gap-2">
                      <Button size="xs" onClick={() => { autoMatchAll(); }}>Auto-match</Button>
                      <Button size="xs" color="gray" onClick={() => setChapterMappings({})}>Clear</Button>
                    </div>
                  </div>

                  <div className="divide-y divide-gray-100 rounded border border-gray-100 dark:divide-gray-700 dark:border-gray-700">
                    {chapters.map((c, idx) => (
                      <div key={idx} className="grid grid-cols-1 gap-3 px-3 py-3 md:grid-cols-5 items-center">
                        <div className="md:col-span-2">
                          <div className="font-medium">{c.name || `Chapter ${idx + 1}`}</div>
                          <div className="mt-1 text-xs text-gray-500">Pages: {c.alignedStart ?? '-'} - {c.alignedEnd ?? '-'}</div>
                        </div>
                        <div className="md:col-span-3">
                          <Label htmlFor={`mapSelect-${idx}`} value="Map to existing chapter (optional)" />
                          <select id={`mapSelect-${idx}`} aria-label={`Map generated chapter ${idx + 1} to existing chapter`} className="mt-1 block w-full rounded border-gray-200 p-2 text-sm" value={chapterMappings[idx] ?? ''} onChange={(e) => {
                            const v = e.target.value ? parseInt(e.target.value, 10) : null;
                            setChapterMappings(prev => ({ ...prev, [idx]: v }));
                          }}>
                            <option value="">-- do not map / create new --</option>
                            {dbChapters.map((d:any) => (<option key={d.id} value={d.id}>{d.name ?? d.title ?? d.id}</option>))}
                          </select>
                          {/* University mode: show course-level subchapters and allow mapping of Gemini-provided subchapters */}
                          {institutionMode === 'university' && (
                            <div className="mt-3 rounded border border-gray-100 bg-gray-50 p-3 text-sm">
                              <div className="mb-2 text-xs text-gray-600">Course subchapters available: {courseSubchapters.length}</div>
                              { (c.subchapters && c.subchapters.length > 0) ? (
                                <div className="space-y-2">
                                  <div className="text-xs text-gray-500 mb-1">Gemini-provided subchapters (map each to a course subchapter):</div>
                                  {c.subchapters.map((sc, sidx) => (
                                    <div key={`sub-${idx}-${sidx}`} className="flex items-center gap-2">
                                      <div className="flex-1 text-sm">{sc.name || `Subchapter ${sidx+1}`}</div>
                                      <select aria-label={`Map subchapter ${sidx+1} of chapter ${idx+1}`} className="text-sm rounded border p-1" value={subchapterMappings[`${idx}_${sidx}`] ?? ''} onChange={(e) => {
                                        const val = e.target.value ? parseInt(e.target.value, 10) : null;
                                        setSubchapterMappings(prev => ({ ...prev, [`${idx}_${sidx}`]: val }));
                                      }}>
                                        <option value="">-- do not map / create new --</option>
                                        {courseSubchapters.map((cs) => (<option key={cs.id} value={cs.id}>{cs.name}</option>))}
                                      </select>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-xs text-gray-500">No Gemini subchapters parsed for this chapter.</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 flex items-center justify-between">
                <Button onClick={() => setCurrentStep(3)} className="rounded-full bg-white text-teal-700 border border-teal-300 hover:bg-teal-50 px-6">Back</Button>
                <div>
                  <Button
                    onClick={() => {
                      // Open modal to choose chunking mode (text/image)
                      setChunkModeChoice('text');
                      setShowChunkModeModal(true);
                    }}
                    disabled={!chapters || chapters.length === 0}
                    className="rounded-full bg-teal-600 hover:bg-teal-700 text-white px-6 shadow-sm"
                  >
                    Next: Chunk & Summary
                  </Button>
                </div>
              </div>
            </div>
          )}
          {currentStep === 5 && (
            <div className="rounded-2xl border border-gray-200/70 bg-white p-6 shadow-sm">
              <h4 className="mb-3 text-base font-semibold">5. Chunk & summary</h4>
              <div className="mb-3 text-sm text-gray-600">Use this step to run chunking (extract pages) and review per-chapter chunk counts before embedding.</div>

              {chunkSummaries && chunkSummaries.length > 0 ? (
                <div className="mb-4">
                  <h5 className="mb-2 font-semibold">Chunk summaries</h5>
                  <div className="space-y-2">
                    {chunkSummaries.map((s, i) => (
                      <div key={i} className="rounded border border-gray-100 p-2 text-sm flex items-center justify-between">
                        <div>{s.name}</div>
                        <div className="text-xs text-gray-500">{s.chunks} pages ({s.start}-{s.end})</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mb-4 rounded border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500">No chunks yet. Run chunking to extract pages.</div>
              )}

              {chunkProgress && chunkProgress.total > 0 && (
                <div className="mb-3">
                  <ProgressBar completed={chunkProgress.completed} total={chunkProgress.total} label={chunkProgress.label} striped />
                </div>
              )}

              <div className="mb-4">
                <Button onClick={handleChunkChapters} disabled={isChunking || !chapters || chapters.length === 0} className="mr-2">Run chunking</Button>
                <Button onClick={() => { setChunkSummaries([]); setChunkItems([]); }} color="gray" size="xs">Clear chunks</Button>
              </div>

              {/* Uploaded files status panel for image/file-based chunking */}
              {uploadedFiles && uploadedFiles.length > 0 && (
                <div className="mb-4 p-4 border rounded bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <strong>Upload status</strong>
                    <div className="flex items-center gap-2">
                      <label htmlFor="uploadConcurrencyInput" className="text-sm">Concurrency</label>
                      <input
                        id="uploadConcurrencyInput"
                        title="Upload concurrency"
                        aria-label="Upload concurrency"
                        type="number"
                        value={uploadConcurrency}
                        min={1}
                        max={10}
                        onChange={(e) => setUploadConcurrency(Number(e.target.value) || 1)}
                        className="w-20 input input-sm"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    {uploadedFiles.map((entry) => (
                      <div key={entry.name} className="p-2 bg-white rounded shadow-sm flex items-center justify-between">
                        <div>
                          <div className="font-medium">{entry.name} {entry.start ? `(${entry.start}-${entry.end})` : ''}</div>
                          <div className="text-sm text-gray-600">State: {entry.state} {entry.error ? `— ${entry.error}` : ''}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          {entry.state === 'FAILED' && (
                            <button className="btn btn-secondary btn-sm" onClick={() => retryUpload(entry.name)}>Retry</button>
                          )}
                          {entry.state === 'READY' && entry.uri && (
                            <a href={entry.uri} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">Open</a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

                  

              <div className="mt-4 flex items-center justify-between">
                <Button onClick={() => setCurrentStep(4)} className="rounded-full bg-white text-teal-700 border border-teal-300 hover:bg-teal-50 px-6">Back</Button>
                <Button
                  onClick={() => { setCurrentStep(6); setTimeout(() => { handleEmbedChunks(); }, 0); }}
                  disabled={!chunkItems || chunkItems.length === 0}
                  className="rounded-full bg-teal-600 hover:bg-teal-700 text-white px-6 shadow-sm"
                >
                  Next: Embed Chunks
                </Button>
              </div>
            </div>
          )}

          {currentStep === 6 && (
            <div className="rounded-2xl border border-gray-200/70 bg-white p-6 shadow-sm">
              <h4 className="mb-3 text-base font-semibold">6. Embed Chunks</h4>
              {embedProgress && embedProgress.total > 0 && (
                <div className="mb-2">
                  <ProgressBar completed={embedProgress.completed} total={embedProgress.total} label={embedProgress.label} striped />
                </div>
              )}

              {!isEmbedding && embedSummaries.length > 0 && (
                <div className="mt-4">
                  <h5 className="mb-2 font-semibold">Embedding summary</h5>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm text-gray-500">Click Preview to inspect example embeddings for a chapter.</div>
                    <div className="flex items-center gap-2">
                      <Button size="xs" onClick={() => {
                        const newMap: Record<string, boolean> = {};
                        for (const s of embedSummaries) newMap[s.chapterName] = true;
                        setOpenPreviewChapters(newMap);
                      }}>Expand all</Button>
                      <Button size="xs" color="gray" onClick={() => setOpenPreviewChapters({})}>Collapse all</Button>
                    </div>
                  </div>

                  <div className="divide-y divide-gray-100 rounded border border-gray-100 dark:divide-gray-700 dark:border-gray-700">
                    {embedSummaries.map((s, i) => {
                      const details = embedDetails?.[s.chapterName] ?? [];
                      const opened = !!openPreviewChapters[s.chapterName];
                      return (
                        <div key={i} className="px-3 py-2">
                          <div className="flex items-center justify-between">
                            <div className="font-medium">{s.chapterName}</div>
                            <div className="flex items-center gap-3">
                              <div className="text-sm">{s.count} embeddings</div>
                              <Button size="xs" onClick={() => setOpenPreviewChapters(prev => ({ ...prev, [s.chapterName]: !prev[s.chapterName] }))}>{opened ? 'Hide' : 'Preview'}</Button>
                            </div>
                          </div>
                          {opened && (
                            <div className="mt-2 space-y-2">
                              {details.length === 0 ? (
                                <div className="text-xs text-gray-500">No example details for this chapter (embedding may not have exposed raw responses).</div>
                              ) : (
                                details.slice(0, 5).map((d, idx) => (
                                  <div key={idx} className="rounded border border-gray-100 p-2 text-xs">
                                    <div className="font-medium">Page {d.page}</div>
                                    <div className="mt-1 text-[13px] text-gray-700 line-clamp-3">{d.text}</div>
                                    <details className="mt-1 text-[11px] text-gray-500">
                                      <summary>Raw response</summary>
                                      <pre className="mt-1 text-[11px] bg-gray-50 p-2 rounded max-h-40 overflow-auto">{JSON.stringify(d.raw, null, 2)}</pre>
                                    </details>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {(!isEmbedding && (!embedSummaries || embedSummaries.length === 0)) && (
                <div className="mt-2 rounded border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  Click "Re-run embedding" to start processing.
                </div>
              )}

              <div className="mt-4 flex items-center justify-between">
                <Button onClick={() => setCurrentStep(5)} className="rounded-full bg-white text-teal-700 border border-teal-300 hover:bg-teal-50 px-6">Back</Button>
                <Button
                  onClick={embedSummaries && embedSummaries.length > 0 ? handleSaveBook : handleEmbedChunks}
                  isProcessing={isEmbedding}
                  disabled={isEmbedding || (chunkItems?.length ?? 0) === 0}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-6 shadow-sm"
                >
                  {isEmbedding ? 'Embedding…' : (embedSummaries && embedSummaries.length > 0 ? 'Finish' : 'Run embedding')}
                </Button>
              </div>
            </div>
          )}
          {/* API Key modal rendered at top level when requested */}
          {showApiKeyModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="mx-4 w-full max-w-md rounded bg-white p-6">
                <h4 className="mb-2 text-lg font-semibold">Add your Gemini API key</h4>
                <p className="mb-3 text-sm text-gray-600">We need your API key to call Gemini for chapter extraction and embeddings. The key is stored for your account only.</p>
                <Label htmlFor="modalGeminiKey" value="Gemini API Key" />
                <TextInput id="modalGeminiKey" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="sk-..." />
                <div className="mt-4 flex justify-end gap-2">
                  <Button color="gray" onClick={() => setShowApiKeyModal(false)}>Close</Button>
                  <Button color="primary" onClick={async () => { await saveApiKeyToServer(apiKey); }} isProcessing={isSavingKey}>{isSavingKey ? 'Saving…' : 'Save'}</Button>
                </div>
              </div>
            </div>
          )}
          {showChunkModeModal && (
            <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
              <div className="mx-4 w-full max-w-md rounded bg-white p-6">
                <h4 className="mb-2 text-lg font-semibold">Chunking mode</h4>
                <p className="mb-3 text-sm text-gray-600">Choose whether to chunk by extracting text from the PDF (fast) or send pages as PDF files (image mode) and ask Gemini to OCR/extract text for each page.</p>
                <div className="mb-3 space-y-2">
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="chunkMode" checked={chunkModeChoice === 'text'} onChange={() => setChunkModeChoice('text')} />
                    <span className="text-sm">Text mode (use pdf.js text extraction)</span>
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="chunkMode" checked={chunkModeChoice === 'image'} onChange={() => setChunkModeChoice('image')} />
                    <span className="text-sm">Image/file mode (create small PDFs and ask Gemini to extract)</span>
                  </label>
                </div>
                {chunkModeChoice === 'image' && (
                  <div className="mb-3">
                    <Label htmlFor="pagesPerUploadModal" value="Pages per upload (PDF)" />
                    <TextInput id="pagesPerUploadModal" type="number" min={1} value={pagesPerUpload} onChange={(e) => setPagesPerUpload(e.target.value)} />
                  </div>
                )}
                <div className="mt-4 flex justify-end gap-2">
                  <Button color="gray" onClick={() => setShowChunkModeModal(false)}>Cancel</Button>
                  <Button color="primary" onClick={async () => {
                    setShowChunkModeModal(false);
                    if (chunkModeChoice === 'text') {
                      setCurrentStep(5);
                      setTimeout(() => { handleChunkChapters(); }, 0);
                    } else {
                      // image mode chunking
                      setCurrentStep(5);
                      setTimeout(() => { handleChunkChaptersImageMode(); }, 0);
                    }
                  }}>{isChunking ? 'Processing…' : 'Start'}</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RagSetup;
